// apps/lynar/src/main.js
//
// Entry point Lynar MVP. Port del JS di apps/v7.3/prototype-athlete-journey_v7_3.html
// (rebrand finale LYNAR di v7.2 + risograph-poster-v2 design system).
//
// Il codice sotto e' quasi-verbatim da v7.3 (3309 righe -> file singolo). La
// trasformazione minima e' l'aggiunta di:
//   - import { supabase } from './supabase.js'  (Sprint 0.4)
// che espone window.supabase per il dev console + abilita auth/storage/db.
//
// Le 4 categorie di delta v7.2 -> v7.3 (~61 righe) sono cosmetic/brand:
//   - Title + JS/CSS header comments (Winter//Trainer -> LYNAR)
//   - Home wordmark L<Y>NAR + tagline "SET BY SET . ALONG THE LINE"
//   - CSS mis-registration shadows hard-locked a red+blu (no theme rotation)
//   - Logo size mobile 42px -> 54px
// Zero cambiamenti su rule engine, stato, workout execution, set logging.
//
// La modularizzazione in moduli ES separati (rule-engine.js, state.js, ui.js)
// e' programmata per Sprint 3.3.

import { supabase } from './supabase.js';

console.log('[lynar] boot — supabase client:', supabase);

/* =============================================================================
 * LYNAR · v7.3 PROTOTYPE  (rebrand from WINTER TRAINER · v3)
 *
 * Validates FEATURE-0001 (MVP execution flow) + FEATURE-0002 (Rule Engine v2).
 *
 * F1 contract honored:
 *   - week + day picker, exercise preview, GO/HOLD card, Done + Early-fatigue,
 *     load confirmation, pre-exercise null-load entry, skip exercise w/ confirm,
 *     Rest screen with countdown ring + next-up load, Workout Completion.
 *
 * F2 rule engine implemented:
 *   §1 Main rules        (signal mapping; underload/optimal/managed/neural/fail)
 *   §2 Severe shortfall  (actual/target<0.5 → -30% rounded 0.5 kg, goal-indep — DL-8)
 *   §3 Cumulative fatigue (3-set sliding window per exercise — DL-12)
 *   §4 Priority auto-skip (cumulative OR neural-fatigue + priority=optional, 5s opt-out — DL-13)
 *   §5 Neural stack       (3+ consecutive neuro_type=neural → rest bump + tolerance reduction — DL-14)
 *   §6 Inter-exercise rest (override only when programmed <120s — DL-11)
 *   §7 Two fields: category (F1) + neuro_type (F2) — DL-10 / F1-AM-8
 *   §8 Dispatcher (single source for status copy)
 *
 *   Goal-aware magnitudes:    hypertrophy ±5%/+30s · strength ±10%/+90s — DL-3
 *   F1 STORY-014 vs F2 §2:    layered, F2 wins on <0.5 ratio — DL-4 / F1-AM-3
 *   DL-9 fallback:            unmapped (effort,tech) → managed_fatigue + telemetry
 * ============================================================================= */

/* -------------------- 1. PROGRAM DATA -------------------- *
 * Source of truth: product/features/FEATURE-0001-MVP-for-validation/sample.json
 * embedded verbatim below. normalizeSample() converts to the prototype's internal
 * shape AND injects F2 augmentations (category rename, neuro_type, priority,
 * exercise-level goal override) per F1-AM-1..8. Edit sample.json upstream — the
 * embed below is a snapshot for self-contained operation.
 * --------------------------------------------------------- */
const RAW_SAMPLE = JSON.parse(`{"athlete": {"id": "ath_angelo_001", "name": "Angelo"}, "program": {"id": "prog_winter_001", "name": "Winter Trainer Demo Program", "duration_weeks": 10, "units": {"load": "kg", "rest": "seconds"}, "workouts": [{"id": "week1_pull", "week": 1, "day": "Tuesday", "name": "Pull Day", "objective": "Explosiveness + real strength on pull-ups, high quality, low cognitive load", "exercise_prescriptions": [{"id": "w1_pull_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_description": "Shoulder and T-spine mobility", "target_duration_seconds": 180}, {"order": 2, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 1"}, {"order": 3, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 2"}, {"order": 4, "target_duration_seconds": 30, "target_notes": "Active dead hang set 1"}, {"order": 5, "target_duration_seconds": 30, "target_notes": "Active dead hang set 2"}]}, {"id": "w1_pull_emom", "exercise_name": "Explosive Pull-up EMOM", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 1, "target_load": 0, "target_notes": "Minute 1"}, {"order": 2, "target_reps": 2, "target_load": 0, "target_notes": "Minute 2"}, {"order": 3, "target_reps": 1, "target_load": 0, "target_notes": "Minute 3"}, {"order": 4, "target_reps": 2, "target_load": 0, "target_notes": "Minute 4"}, {"order": 5, "target_reps": 1, "target_load": 0, "target_notes": "Minute 5"}, {"order": 6, "target_reps": 2, "target_load": 0, "target_notes": "Minute 6"}]}, {"id": "w1_pull_main", "exercise_name": "Weighted Pull-up with Stops", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 5, "target_load": 0, "target_percentage": 72, "rest_after_set_seconds": 150, "target_notes": "Week 1 progression"}, {"order": 2, "target_reps": 5, "target_load": 0, "target_percentage": 72, "rest_after_set_seconds": 150, "target_notes": "Week 1 progression"}, {"order": 3, "target_reps": 5, "target_load": 0, "target_percentage": 72, "rest_after_set_seconds": 150, "target_notes": "Week 1 progression"}, {"order": 4, "target_reps": 5, "target_load": 0, "target_percentage": 72, "rest_after_set_seconds": 150, "target_notes": "Week 1 progression"}, {"order": 5, "target_reps": 5, "target_load": 0, "target_percentage": 72, "rest_after_set_seconds": 60, "target_notes": "Week 1 progression"}]}, {"id": "w1_pull_accessory", "exercise_name": "Neutral Grip Lat Machine", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 2, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 3, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}]}, {"id": "w1_pull_military", "exercise_name": "Dumbbell Military Press", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w1_pull_biceps", "exercise_name": "Cable Curl", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}]}, {"id": "w1_pull_core", "exercise_name": "Plank", "type": "core", "inter_exercise_rest_seconds": 0, "set_blocks": [{"order": 1, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 2, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 3, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 4, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 5, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 6, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 7, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 8, "target_duration_seconds": 30, "rest_after_set_seconds": 30}]}]}, {"id": "week1_push", "week": 1, "day": "Thursday", "name": "Push Day", "objective": "Real dip strength, sustainable pushing volume, protected shoulders and elbows", "exercise_prescriptions": [{"id": "w1_push_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_duration_seconds": 600, "target_notes": "Bike"}, {"order": 2, "target_duration_seconds": 180, "target_notes": "Push-up + Jumping Jack"}]}, {"id": "w1_push_simil_mu", "exercise_name": "Simil Muscle-Up", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 0, "target_notes": "Slow and controlled, 1s pause on top"}]}, {"id": "w1_push_main", "exercise_name": "Weighted Dip", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 5, "target_load": 6, "target_percentage": 72, "rest_after_set_seconds": 150, "target_notes": "Week 1 progression"}, {"order": 2, "target_reps": 5, "target_load": 6, "target_percentage": 72, "rest_after_set_seconds": 150, "target_notes": "Week 1 progression"}, {"order": 3, "target_reps": 5, "target_load": 6, "target_percentage": 72, "rest_after_set_seconds": 150, "target_notes": "Week 1 progression"}, {"order": 4, "target_reps": 5, "target_load": 6, "target_percentage": 72, "rest_after_set_seconds": 150, "target_notes": "Week 1 progression"}, {"order": 5, "target_reps": 5, "target_load": 6, "target_percentage": 72, "rest_after_set_seconds": 60, "target_notes": "Week 1 progression"}]}, {"id": "w1_push_complementary", "exercise_name": "Dumbbell Bench Press", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w1_push_bardip", "exercise_name": "Bar Dip", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}]}, {"id": "w1_push_core", "exercise_name": "Sit-up", "type": "core", "inter_exercise_rest_seconds": 30, "set_blocks": [{"order": 1, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 2, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 3, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}]}]}, {"id": "week2_pull", "week": 2, "day": "Tuesday", "name": "Pull Day", "objective": "Explosiveness + real strength on pull-ups, high quality, low cognitive load", "exercise_prescriptions": [{"id": "w2_pull_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_description": "Shoulder and T-spine mobility", "target_duration_seconds": 180}, {"order": 2, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 1"}, {"order": 3, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 2"}, {"order": 4, "target_duration_seconds": 30, "target_notes": "Active dead hang set 1"}, {"order": 5, "target_duration_seconds": 30, "target_notes": "Active dead hang set 2"}]}, {"id": "w2_pull_emom", "exercise_name": "Explosive Pull-up EMOM", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 1, "target_load": 0, "target_notes": "Minute 1"}, {"order": 2, "target_reps": 2, "target_load": 0, "target_notes": "Minute 2"}, {"order": 3, "target_reps": 1, "target_load": 0, "target_notes": "Minute 3"}, {"order": 4, "target_reps": 2, "target_load": 0, "target_notes": "Minute 4"}, {"order": 5, "target_reps": 1, "target_load": 0, "target_notes": "Minute 5"}, {"order": 6, "target_reps": 2, "target_load": 0, "target_notes": "Minute 6"}]}, {"id": "w2_pull_main", "exercise_name": "Weighted Pull-up with Stops", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 5, "target_load": 0, "target_percentage": 74, "rest_after_set_seconds": 150, "target_notes": "Week 2 progression"}, {"order": 2, "target_reps": 5, "target_load": 0, "target_percentage": 74, "rest_after_set_seconds": 150, "target_notes": "Week 2 progression"}, {"order": 3, "target_reps": 5, "target_load": 0, "target_percentage": 74, "rest_after_set_seconds": 150, "target_notes": "Week 2 progression"}, {"order": 4, "target_reps": 5, "target_load": 0, "target_percentage": 74, "rest_after_set_seconds": 150, "target_notes": "Week 2 progression"}, {"order": 5, "target_reps": 5, "target_load": 0, "target_percentage": 74, "rest_after_set_seconds": 60, "target_notes": "Week 2 progression"}]}, {"id": "w2_pull_accessory", "exercise_name": "Neutral Grip Lat Machine", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 2, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 3, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}]}, {"id": "w2_pull_military", "exercise_name": "Dumbbell Military Press", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w2_pull_biceps", "exercise_name": "Cable Curl", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}]}, {"id": "w2_pull_core", "exercise_name": "Plank", "type": "core", "inter_exercise_rest_seconds": 0, "set_blocks": [{"order": 1, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 2, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 3, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 4, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 5, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 6, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 7, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 8, "target_duration_seconds": 30, "rest_after_set_seconds": 30}]}]}, {"id": "week2_push", "week": 2, "day": "Thursday", "name": "Push Day", "objective": "Real dip strength, sustainable pushing volume, protected shoulders and elbows", "exercise_prescriptions": [{"id": "w2_push_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_duration_seconds": 600, "target_notes": "Bike"}, {"order": 2, "target_duration_seconds": 180, "target_notes": "Push-up + Jumping Jack"}]}, {"id": "w2_push_simil_mu", "exercise_name": "Simil Muscle-Up", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 0, "target_notes": "Slow and controlled, 1s pause on top"}]}, {"id": "w2_push_main", "exercise_name": "Weighted Dip", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 5, "target_load": 8, "target_percentage": 74, "rest_after_set_seconds": 150, "target_notes": "Week 2 progression"}, {"order": 2, "target_reps": 5, "target_load": 8, "target_percentage": 74, "rest_after_set_seconds": 150, "target_notes": "Week 2 progression"}, {"order": 3, "target_reps": 5, "target_load": 8, "target_percentage": 74, "rest_after_set_seconds": 150, "target_notes": "Week 2 progression"}, {"order": 4, "target_reps": 5, "target_load": 8, "target_percentage": 74, "rest_after_set_seconds": 150, "target_notes": "Week 2 progression"}, {"order": 5, "target_reps": 5, "target_load": 8, "target_percentage": 74, "rest_after_set_seconds": 60, "target_notes": "Week 2 progression"}]}, {"id": "w2_push_complementary", "exercise_name": "Dumbbell Bench Press", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w2_push_bardip", "exercise_name": "Bar Dip", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}]}, {"id": "w2_push_core", "exercise_name": "Sit-up", "type": "core", "inter_exercise_rest_seconds": 30, "set_blocks": [{"order": 1, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 2, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 3, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}]}]}, {"id": "week3_pull", "week": 3, "day": "Tuesday", "name": "Pull Day", "objective": "Explosiveness + real strength on pull-ups, high quality, low cognitive load", "exercise_prescriptions": [{"id": "w3_pull_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_description": "Shoulder and T-spine mobility", "target_duration_seconds": 180}, {"order": 2, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 1"}, {"order": 3, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 2"}, {"order": 4, "target_duration_seconds": 30, "target_notes": "Active dead hang set 1"}, {"order": 5, "target_duration_seconds": 30, "target_notes": "Active dead hang set 2"}]}, {"id": "w3_pull_emom", "exercise_name": "Explosive Pull-up EMOM", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 1, "target_load": 0, "target_notes": "Minute 1"}, {"order": 2, "target_reps": 2, "target_load": 0, "target_notes": "Minute 2"}, {"order": 3, "target_reps": 1, "target_load": 0, "target_notes": "Minute 3"}, {"order": 4, "target_reps": 2, "target_load": 0, "target_notes": "Minute 4"}, {"order": 5, "target_reps": 1, "target_load": 0, "target_notes": "Minute 5"}, {"order": 6, "target_reps": 2, "target_load": 0, "target_notes": "Minute 6"}]}, {"id": "w3_pull_main", "exercise_name": "Weighted Pull-up with Stops", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 5, "target_load": 0, "target_percentage": 76, "rest_after_set_seconds": 150, "target_notes": "Week 3 progression"}, {"order": 2, "target_reps": 5, "target_load": 0, "target_percentage": 76, "rest_after_set_seconds": 150, "target_notes": "Week 3 progression"}, {"order": 3, "target_reps": 5, "target_load": 0, "target_percentage": 76, "rest_after_set_seconds": 150, "target_notes": "Week 3 progression"}, {"order": 4, "target_reps": 5, "target_load": 0, "target_percentage": 76, "rest_after_set_seconds": 150, "target_notes": "Week 3 progression"}, {"order": 5, "target_reps": 5, "target_load": 0, "target_percentage": 76, "rest_after_set_seconds": 60, "target_notes": "Week 3 progression"}]}, {"id": "w3_pull_accessory", "exercise_name": "Neutral Grip Lat Machine", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 2, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 3, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}]}, {"id": "w3_pull_military", "exercise_name": "Dumbbell Military Press", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w3_pull_biceps", "exercise_name": "Cable Curl", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}]}, {"id": "w3_pull_core", "exercise_name": "Plank", "type": "core", "inter_exercise_rest_seconds": 0, "set_blocks": [{"order": 1, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 2, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 3, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 4, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 5, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 6, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 7, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 8, "target_duration_seconds": 30, "rest_after_set_seconds": 30}]}]}, {"id": "week3_push", "week": 3, "day": "Thursday", "name": "Push Day", "objective": "Real dip strength, sustainable pushing volume, protected shoulders and elbows", "exercise_prescriptions": [{"id": "w3_push_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_duration_seconds": 600, "target_notes": "Bike"}, {"order": 2, "target_duration_seconds": 180, "target_notes": "Push-up + Jumping Jack"}]}, {"id": "w3_push_simil_mu", "exercise_name": "Simil Muscle-Up", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 0, "target_notes": "Slow and controlled, 1s pause on top"}]}, {"id": "w3_push_main", "exercise_name": "Weighted Dip", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 5, "target_load": 10, "target_percentage": 76, "rest_after_set_seconds": 150, "target_notes": "Week 3 progression"}, {"order": 2, "target_reps": 5, "target_load": 10, "target_percentage": 76, "rest_after_set_seconds": 150, "target_notes": "Week 3 progression"}, {"order": 3, "target_reps": 5, "target_load": 10, "target_percentage": 76, "rest_after_set_seconds": 150, "target_notes": "Week 3 progression"}, {"order": 4, "target_reps": 5, "target_load": 10, "target_percentage": 76, "rest_after_set_seconds": 150, "target_notes": "Week 3 progression"}, {"order": 5, "target_reps": 5, "target_load": 10, "target_percentage": 76, "rest_after_set_seconds": 60, "target_notes": "Week 3 progression"}]}, {"id": "w3_push_complementary", "exercise_name": "Dumbbell Bench Press", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w3_push_bardip", "exercise_name": "Bar Dip", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}]}, {"id": "w3_push_core", "exercise_name": "Sit-up", "type": "core", "inter_exercise_rest_seconds": 30, "set_blocks": [{"order": 1, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 2, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 3, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}]}]}, {"id": "week4_pull", "week": 4, "day": "Tuesday", "name": "Pull Day", "objective": "Explosiveness + real strength on pull-ups, high quality, low cognitive load", "exercise_prescriptions": [{"id": "w4_pull_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_description": "Shoulder and T-spine mobility", "target_duration_seconds": 180}, {"order": 2, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 1"}, {"order": 3, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 2"}, {"order": 4, "target_duration_seconds": 30, "target_notes": "Active dead hang set 1"}, {"order": 5, "target_duration_seconds": 30, "target_notes": "Active dead hang set 2"}]}, {"id": "w4_pull_emom", "exercise_name": "Explosive Pull-up EMOM", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 1, "target_load": 0, "target_notes": "Minute 1"}, {"order": 2, "target_reps": 2, "target_load": 0, "target_notes": "Minute 2"}, {"order": 3, "target_reps": 1, "target_load": 0, "target_notes": "Minute 3"}, {"order": 4, "target_reps": 2, "target_load": 0, "target_notes": "Minute 4"}, {"order": 5, "target_reps": 1, "target_load": 0, "target_notes": "Minute 5"}, {"order": 6, "target_reps": 2, "target_load": 0, "target_notes": "Minute 6"}]}, {"id": "w4_pull_main", "exercise_name": "Weighted Pull-up with Stops", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 5, "target_load": 2, "target_percentage": 78, "rest_after_set_seconds": 150, "target_notes": "Week 4 progression"}, {"order": 2, "target_reps": 5, "target_load": 2, "target_percentage": 78, "rest_after_set_seconds": 150, "target_notes": "Week 4 progression"}, {"order": 3, "target_reps": 5, "target_load": 2, "target_percentage": 78, "rest_after_set_seconds": 150, "target_notes": "Week 4 progression"}, {"order": 4, "target_reps": 5, "target_load": 2, "target_percentage": 78, "rest_after_set_seconds": 150, "target_notes": "Week 4 progression"}, {"order": 5, "target_reps": 5, "target_load": 2, "target_percentage": 78, "rest_after_set_seconds": 60, "target_notes": "Week 4 progression"}]}, {"id": "w4_pull_accessory", "exercise_name": "Neutral Grip Lat Machine", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 2, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 3, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}]}, {"id": "w4_pull_military", "exercise_name": "Dumbbell Military Press", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w4_pull_biceps", "exercise_name": "Cable Curl", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}]}, {"id": "w4_pull_core", "exercise_name": "Plank", "type": "core", "inter_exercise_rest_seconds": 0, "set_blocks": [{"order": 1, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 2, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 3, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 4, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 5, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 6, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 7, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 8, "target_duration_seconds": 30, "rest_after_set_seconds": 30}]}]}, {"id": "week4_push", "week": 4, "day": "Thursday", "name": "Push Day", "objective": "Real dip strength, sustainable pushing volume, protected shoulders and elbows", "exercise_prescriptions": [{"id": "w4_push_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_duration_seconds": 600, "target_notes": "Bike"}, {"order": 2, "target_duration_seconds": 180, "target_notes": "Push-up + Jumping Jack"}]}, {"id": "w4_push_simil_mu", "exercise_name": "Simil Muscle-Up", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 0, "target_notes": "Slow and controlled, 1s pause on top"}]}, {"id": "w4_push_main", "exercise_name": "Weighted Dip", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 5, "target_load": 12, "target_percentage": 78, "rest_after_set_seconds": 150, "target_notes": "Week 4 progression"}, {"order": 2, "target_reps": 5, "target_load": 12, "target_percentage": 78, "rest_after_set_seconds": 150, "target_notes": "Week 4 progression"}, {"order": 3, "target_reps": 5, "target_load": 12, "target_percentage": 78, "rest_after_set_seconds": 150, "target_notes": "Week 4 progression"}, {"order": 4, "target_reps": 5, "target_load": 12, "target_percentage": 78, "rest_after_set_seconds": 150, "target_notes": "Week 4 progression"}, {"order": 5, "target_reps": 5, "target_load": 12, "target_percentage": 78, "rest_after_set_seconds": 60, "target_notes": "Week 4 progression"}]}, {"id": "w4_push_complementary", "exercise_name": "Dumbbell Bench Press", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w4_push_bardip", "exercise_name": "Bar Dip", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}]}, {"id": "w4_push_core", "exercise_name": "Sit-up", "type": "core", "inter_exercise_rest_seconds": 30, "set_blocks": [{"order": 1, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 2, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 3, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}]}]}, {"id": "week5_pull", "week": 5, "day": "Tuesday", "name": "Pull Day", "objective": "Explosiveness + real strength on pull-ups, high quality, low cognitive load", "exercise_prescriptions": [{"id": "w5_pull_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_description": "Shoulder and T-spine mobility", "target_duration_seconds": 180}, {"order": 2, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 1"}, {"order": 3, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 2"}, {"order": 4, "target_duration_seconds": 30, "target_notes": "Active dead hang set 1"}, {"order": 5, "target_duration_seconds": 30, "target_notes": "Active dead hang set 2"}]}, {"id": "w5_pull_emom", "exercise_name": "Explosive Pull-up EMOM", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 1, "target_load": 0, "target_notes": "Minute 1"}, {"order": 2, "target_reps": 2, "target_load": 0, "target_notes": "Minute 2"}, {"order": 3, "target_reps": 1, "target_load": 0, "target_notes": "Minute 3"}, {"order": 4, "target_reps": 2, "target_load": 0, "target_notes": "Minute 4"}, {"order": 5, "target_reps": 1, "target_load": 0, "target_notes": "Minute 5"}, {"order": 6, "target_reps": 2, "target_load": 0, "target_notes": "Minute 6"}]}, {"id": "w5_pull_main", "exercise_name": "Weighted Pull-up with Stops", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 4, "target_load": 6, "target_percentage": 82, "rest_after_set_seconds": 150, "target_notes": "Week 5 progression"}, {"order": 2, "target_reps": 4, "target_load": 6, "target_percentage": 82, "rest_after_set_seconds": 150, "target_notes": "Week 5 progression"}, {"order": 3, "target_reps": 4, "target_load": 6, "target_percentage": 82, "rest_after_set_seconds": 150, "target_notes": "Week 5 progression"}, {"order": 4, "target_reps": 4, "target_load": 6, "target_percentage": 82, "rest_after_set_seconds": 60, "target_notes": "Week 5 progression"}]}, {"id": "w5_pull_accessory", "exercise_name": "Neutral Grip Lat Machine", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 2, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 3, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}]}, {"id": "w5_pull_military", "exercise_name": "Dumbbell Military Press", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w5_pull_biceps", "exercise_name": "Cable Curl", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}]}, {"id": "w5_pull_core", "exercise_name": "Plank", "type": "core", "inter_exercise_rest_seconds": 0, "set_blocks": [{"order": 1, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 2, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 3, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 4, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 5, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 6, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 7, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 8, "target_duration_seconds": 30, "rest_after_set_seconds": 30}]}]}, {"id": "week5_push", "week": 5, "day": "Thursday", "name": "Push Day", "objective": "Real dip strength, sustainable pushing volume, protected shoulders and elbows", "exercise_prescriptions": [{"id": "w5_push_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_duration_seconds": 600, "target_notes": "Bike"}, {"order": 2, "target_duration_seconds": 180, "target_notes": "Push-up + Jumping Jack"}]}, {"id": "w5_push_simil_mu", "exercise_name": "Simil Muscle-Up", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 0, "target_notes": "Slow and controlled, 1s pause on top"}]}, {"id": "w5_push_main", "exercise_name": "Weighted Dip", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 4, "target_load": 18, "target_percentage": 82, "rest_after_set_seconds": 150, "target_notes": "Week 5 progression"}, {"order": 2, "target_reps": 4, "target_load": 18, "target_percentage": 82, "rest_after_set_seconds": 150, "target_notes": "Week 5 progression"}, {"order": 3, "target_reps": 4, "target_load": 18, "target_percentage": 82, "rest_after_set_seconds": 150, "target_notes": "Week 5 progression"}, {"order": 4, "target_reps": 4, "target_load": 18, "target_percentage": 82, "rest_after_set_seconds": 60, "target_notes": "Week 5 progression"}]}, {"id": "w5_push_complementary", "exercise_name": "Dumbbell Bench Press", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w5_push_bardip", "exercise_name": "Bar Dip", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}]}, {"id": "w5_push_core", "exercise_name": "Sit-up", "type": "core", "inter_exercise_rest_seconds": 30, "set_blocks": [{"order": 1, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 2, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 3, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}]}]}, {"id": "week6_pull", "week": 6, "day": "Tuesday", "name": "Pull Day", "objective": "Explosiveness + real strength on pull-ups, high quality, low cognitive load", "exercise_prescriptions": [{"id": "w6_pull_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_description": "Shoulder and T-spine mobility", "target_duration_seconds": 180}, {"order": 2, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 1"}, {"order": 3, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 2"}, {"order": 4, "target_duration_seconds": 30, "target_notes": "Active dead hang set 1"}, {"order": 5, "target_duration_seconds": 30, "target_notes": "Active dead hang set 2"}]}, {"id": "w6_pull_emom", "exercise_name": "Explosive Pull-up EMOM", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 1, "target_load": 0, "target_notes": "Minute 1"}, {"order": 2, "target_reps": 2, "target_load": 0, "target_notes": "Minute 2"}, {"order": 3, "target_reps": 1, "target_load": 0, "target_notes": "Minute 3"}, {"order": 4, "target_reps": 2, "target_load": 0, "target_notes": "Minute 4"}, {"order": 5, "target_reps": 1, "target_load": 0, "target_notes": "Minute 5"}, {"order": 6, "target_reps": 2, "target_load": 0, "target_notes": "Minute 6"}]}, {"id": "w6_pull_main", "exercise_name": "Weighted Pull-up with Stops", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 4, "target_load": 8, "target_percentage": 84, "rest_after_set_seconds": 150, "target_notes": "Week 6 progression"}, {"order": 2, "target_reps": 4, "target_load": 8, "target_percentage": 84, "rest_after_set_seconds": 150, "target_notes": "Week 6 progression"}, {"order": 3, "target_reps": 4, "target_load": 8, "target_percentage": 84, "rest_after_set_seconds": 150, "target_notes": "Week 6 progression"}, {"order": 4, "target_reps": 4, "target_load": 8, "target_percentage": 84, "rest_after_set_seconds": 60, "target_notes": "Week 6 progression"}]}, {"id": "w6_pull_accessory", "exercise_name": "Neutral Grip Lat Machine", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 2, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 3, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}]}, {"id": "w6_pull_military", "exercise_name": "Dumbbell Military Press", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w6_pull_biceps", "exercise_name": "Cable Curl", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}]}, {"id": "w6_pull_core", "exercise_name": "Plank", "type": "core", "inter_exercise_rest_seconds": 0, "set_blocks": [{"order": 1, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 2, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 3, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 4, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 5, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 6, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 7, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 8, "target_duration_seconds": 30, "rest_after_set_seconds": 30}]}]}, {"id": "week6_push", "week": 6, "day": "Thursday", "name": "Push Day", "objective": "Real dip strength, sustainable pushing volume, protected shoulders and elbows", "exercise_prescriptions": [{"id": "w6_push_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_duration_seconds": 600, "target_notes": "Bike"}, {"order": 2, "target_duration_seconds": 180, "target_notes": "Push-up + Jumping Jack"}]}, {"id": "w6_push_simil_mu", "exercise_name": "Simil Muscle-Up", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 0, "target_notes": "Slow and controlled, 1s pause on top"}]}, {"id": "w6_push_main", "exercise_name": "Weighted Dip", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 4, "target_load": 18, "target_percentage": 84, "rest_after_set_seconds": 150, "target_notes": "Week 6 progression"}, {"order": 2, "target_reps": 4, "target_load": 18, "target_percentage": 84, "rest_after_set_seconds": 150, "target_notes": "Week 6 progression"}, {"order": 3, "target_reps": 4, "target_load": 18, "target_percentage": 84, "rest_after_set_seconds": 150, "target_notes": "Week 6 progression"}, {"order": 4, "target_reps": 4, "target_load": 18, "target_percentage": 84, "rest_after_set_seconds": 60, "target_notes": "Week 6 progression"}]}, {"id": "w6_push_complementary", "exercise_name": "Dumbbell Bench Press", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w6_push_bardip", "exercise_name": "Bar Dip", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}]}, {"id": "w6_push_core", "exercise_name": "Sit-up", "type": "core", "inter_exercise_rest_seconds": 30, "set_blocks": [{"order": 1, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 2, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 3, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}]}]}, {"id": "week7_pull", "week": 7, "day": "Tuesday", "name": "Pull Day", "objective": "Explosiveness + real strength on pull-ups, high quality, low cognitive load", "exercise_prescriptions": [{"id": "w7_pull_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_description": "Shoulder and T-spine mobility", "target_duration_seconds": 180}, {"order": 2, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 1"}, {"order": 3, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 2"}, {"order": 4, "target_duration_seconds": 30, "target_notes": "Active dead hang set 1"}, {"order": 5, "target_duration_seconds": 30, "target_notes": "Active dead hang set 2"}]}, {"id": "w7_pull_emom", "exercise_name": "Explosive Pull-up EMOM", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 1, "target_load": 0, "target_notes": "Minute 1"}, {"order": 2, "target_reps": 2, "target_load": 0, "target_notes": "Minute 2"}, {"order": 3, "target_reps": 1, "target_load": 0, "target_notes": "Minute 3"}, {"order": 4, "target_reps": 2, "target_load": 0, "target_notes": "Minute 4"}, {"order": 5, "target_reps": 1, "target_load": 0, "target_notes": "Minute 5"}, {"order": 6, "target_reps": 2, "target_load": 0, "target_notes": "Minute 6"}]}, {"id": "w7_pull_main", "exercise_name": "Weighted Pull-up with Stops", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 4, "target_load": 10, "target_percentage": 86, "rest_after_set_seconds": 150, "target_notes": "Week 7 progression"}, {"order": 2, "target_reps": 4, "target_load": 10, "target_percentage": 86, "rest_after_set_seconds": 150, "target_notes": "Week 7 progression"}, {"order": 3, "target_reps": 4, "target_load": 10, "target_percentage": 86, "rest_after_set_seconds": 150, "target_notes": "Week 7 progression"}, {"order": 4, "target_reps": 4, "target_load": 10, "target_percentage": 86, "rest_after_set_seconds": 60, "target_notes": "Week 7 progression"}]}, {"id": "w7_pull_accessory", "exercise_name": "Neutral Grip Lat Machine", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 2, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 3, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}]}, {"id": "w7_pull_military", "exercise_name": "Dumbbell Military Press", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w7_pull_biceps", "exercise_name": "Cable Curl", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}]}, {"id": "w7_pull_core", "exercise_name": "Plank", "type": "core", "inter_exercise_rest_seconds": 0, "set_blocks": [{"order": 1, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 2, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 3, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 4, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 5, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 6, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 7, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 8, "target_duration_seconds": 30, "rest_after_set_seconds": 30}]}]}, {"id": "week7_push", "week": 7, "day": "Thursday", "name": "Push Day", "objective": "Real dip strength, sustainable pushing volume, protected shoulders and elbows", "exercise_prescriptions": [{"id": "w7_push_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_duration_seconds": 600, "target_notes": "Bike"}, {"order": 2, "target_duration_seconds": 180, "target_notes": "Push-up + Jumping Jack"}]}, {"id": "w7_push_simil_mu", "exercise_name": "Simil Muscle-Up", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 0, "target_notes": "Slow and controlled, 1s pause on top"}]}, {"id": "w7_push_main", "exercise_name": "Weighted Dip", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 4, "target_load": 20, "target_percentage": 86, "rest_after_set_seconds": 150, "target_notes": "Week 7 progression"}, {"order": 2, "target_reps": 4, "target_load": 20, "target_percentage": 86, "rest_after_set_seconds": 150, "target_notes": "Week 7 progression"}, {"order": 3, "target_reps": 4, "target_load": 20, "target_percentage": 86, "rest_after_set_seconds": 150, "target_notes": "Week 7 progression"}, {"order": 4, "target_reps": 4, "target_load": 20, "target_percentage": 86, "rest_after_set_seconds": 60, "target_notes": "Week 7 progression"}]}, {"id": "w7_push_complementary", "exercise_name": "Dumbbell Bench Press", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w7_push_bardip", "exercise_name": "Bar Dip", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}]}, {"id": "w7_push_core", "exercise_name": "Sit-up", "type": "core", "inter_exercise_rest_seconds": 30, "set_blocks": [{"order": 1, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 2, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 3, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}]}]}, {"id": "week8_pull", "week": 8, "day": "Tuesday", "name": "Pull Day", "objective": "Explosiveness + real strength on pull-ups, high quality, low cognitive load", "exercise_prescriptions": [{"id": "w8_pull_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_description": "Shoulder and T-spine mobility", "target_duration_seconds": 180}, {"order": 2, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 1"}, {"order": 3, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 2"}, {"order": 4, "target_duration_seconds": 30, "target_notes": "Active dead hang set 1"}, {"order": 5, "target_duration_seconds": 30, "target_notes": "Active dead hang set 2"}]}, {"id": "w8_pull_emom", "exercise_name": "Explosive Pull-up EMOM", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 1, "target_load": 0, "target_notes": "Minute 1"}, {"order": 2, "target_reps": 2, "target_load": 0, "target_notes": "Minute 2"}, {"order": 3, "target_reps": 1, "target_load": 0, "target_notes": "Minute 3"}, {"order": 4, "target_reps": 2, "target_load": 0, "target_notes": "Minute 4"}, {"order": 5, "target_reps": 1, "target_load": 0, "target_notes": "Minute 5"}, {"order": 6, "target_reps": 2, "target_load": 0, "target_notes": "Minute 6"}]}, {"id": "w8_pull_main", "exercise_name": "Weighted Pull-up with Stops", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 6, "target_load": 0, "target_percentage": 72, "rest_after_set_seconds": 150, "target_notes": "Week 8 progression"}, {"order": 2, "target_reps": 4, "target_load": 2, "target_percentage": 78, "rest_after_set_seconds": 150, "target_notes": "Week 8 progression"}, {"order": 3, "target_reps": 2, "target_load": 8, "target_percentage": 84, "rest_after_set_seconds": 180, "target_notes": "Week 8 progression"}, {"order": 4, "target_reps": 1, "target_load": 15, "target_percentage": 92, "rest_after_set_seconds": 180, "target_notes": "Week 8 progression"}]}, {"id": "w8_pull_accessory", "exercise_name": "Neutral Grip Lat Machine", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 2, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 3, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}]}, {"id": "w8_pull_military", "exercise_name": "Dumbbell Military Press", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w8_pull_biceps", "exercise_name": "Cable Curl", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}]}, {"id": "w8_pull_core", "exercise_name": "Plank", "type": "core", "inter_exercise_rest_seconds": 0, "set_blocks": [{"order": 1, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 2, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 3, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 4, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 5, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 6, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 7, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 8, "target_duration_seconds": 30, "rest_after_set_seconds": 30}]}]}, {"id": "week8_push", "week": 8, "day": "Thursday", "name": "Push Day", "objective": "Real dip strength, sustainable pushing volume, protected shoulders and elbows", "exercise_prescriptions": [{"id": "w8_push_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_duration_seconds": 600, "target_notes": "Bike"}, {"order": 2, "target_duration_seconds": 180, "target_notes": "Push-up + Jumping Jack"}]}, {"id": "w8_push_simil_mu", "exercise_name": "Simil Muscle-Up", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 0, "target_notes": "Slow and controlled, 1s pause on top"}]}, {"id": "w8_push_main", "exercise_name": "Weighted Dip", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 6, "target_load": 6, "target_percentage": 72, "rest_after_set_seconds": 150, "target_notes": "Week 8 progression"}, {"order": 2, "target_reps": 4, "target_load": 12, "target_percentage": 78, "rest_after_set_seconds": 150, "target_notes": "Week 8 progression"}, {"order": 3, "target_reps": 2, "target_load": 18, "target_percentage": 84, "rest_after_set_seconds": 180, "target_notes": "Week 8 progression"}, {"order": 4, "target_reps": 1, "target_load": 27, "target_percentage": 92, "rest_after_set_seconds": 180, "target_notes": "Week 8 progression"}]}, {"id": "w8_push_complementary", "exercise_name": "Dumbbell Bench Press", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w8_push_bardip", "exercise_name": "Bar Dip", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}]}, {"id": "w8_push_core", "exercise_name": "Sit-up", "type": "core", "inter_exercise_rest_seconds": 30, "set_blocks": [{"order": 1, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 2, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 3, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}]}]}, {"id": "week9_pull", "week": 9, "day": "Tuesday", "name": "Pull Day", "objective": "Explosiveness + real strength on pull-ups, high quality, low cognitive load", "exercise_prescriptions": [{"id": "w9_pull_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_description": "Shoulder and T-spine mobility", "target_duration_seconds": 180}, {"order": 2, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 1"}, {"order": 3, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 2"}, {"order": 4, "target_duration_seconds": 30, "target_notes": "Active dead hang set 1"}, {"order": 5, "target_duration_seconds": 30, "target_notes": "Active dead hang set 2"}]}, {"id": "w9_pull_emom", "exercise_name": "Explosive Pull-up EMOM", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 1, "target_load": 0, "target_notes": "Minute 1"}, {"order": 2, "target_reps": 2, "target_load": 0, "target_notes": "Minute 2"}, {"order": 3, "target_reps": 1, "target_load": 0, "target_notes": "Minute 3"}, {"order": 4, "target_reps": 2, "target_load": 0, "target_notes": "Minute 4"}, {"order": 5, "target_reps": 1, "target_load": 0, "target_notes": "Minute 5"}, {"order": 6, "target_reps": 2, "target_load": 0, "target_notes": "Minute 6"}]}, {"id": "w9_pull_main", "exercise_name": "Weighted Pull-up with Stops", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 6, "target_load": 0, "target_percentage": 76, "rest_after_set_seconds": 150, "target_notes": "Week 9 progression"}, {"order": 2, "target_reps": 4, "target_load": 6, "target_percentage": 82, "rest_after_set_seconds": 150, "target_notes": "Week 9 progression"}, {"order": 3, "target_reps": 2, "target_load": 11, "target_percentage": 88, "rest_after_set_seconds": 180, "target_notes": "Week 9 progression"}, {"order": 4, "target_reps": 1, "target_load": 19, "target_percentage": 96, "rest_after_set_seconds": 180, "target_notes": "Week 9 progression"}]}, {"id": "w9_pull_accessory", "exercise_name": "Neutral Grip Lat Machine", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 2, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 3, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}]}, {"id": "w9_pull_military", "exercise_name": "Dumbbell Military Press", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w9_pull_biceps", "exercise_name": "Cable Curl", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}]}, {"id": "w9_pull_core", "exercise_name": "Plank", "type": "core", "inter_exercise_rest_seconds": 0, "set_blocks": [{"order": 1, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 2, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 3, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 4, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 5, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 6, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 7, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 8, "target_duration_seconds": 30, "rest_after_set_seconds": 30}]}]}, {"id": "week9_push", "week": 9, "day": "Thursday", "name": "Push Day", "objective": "Real dip strength, sustainable pushing volume, protected shoulders and elbows", "exercise_prescriptions": [{"id": "w9_push_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_duration_seconds": 600, "target_notes": "Bike"}, {"order": 2, "target_duration_seconds": 180, "target_notes": "Push-up + Jumping Jack"}]}, {"id": "w9_push_simil_mu", "exercise_name": "Simil Muscle-Up", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 0, "target_notes": "Slow and controlled, 1s pause on top"}]}, {"id": "w9_push_main", "exercise_name": "Weighted Dip", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 6, "target_load": 10, "target_percentage": 76, "rest_after_set_seconds": 150, "target_notes": "Week 9 progression"}, {"order": 2, "target_reps": 4, "target_load": 16, "target_percentage": 82, "rest_after_set_seconds": 150, "target_notes": "Week 9 progression"}, {"order": 3, "target_reps": 2, "target_load": 22, "target_percentage": 88, "rest_after_set_seconds": 180, "target_notes": "Week 9 progression"}, {"order": 4, "target_reps": 1, "target_load": 31, "target_percentage": 96, "rest_after_set_seconds": 180, "target_notes": "Week 9 progression"}]}, {"id": "w9_push_complementary", "exercise_name": "Dumbbell Bench Press", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w9_push_bardip", "exercise_name": "Bar Dip", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}]}, {"id": "w9_push_core", "exercise_name": "Sit-up", "type": "core", "inter_exercise_rest_seconds": 30, "set_blocks": [{"order": 1, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 2, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 3, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}]}]}, {"id": "week10_pull", "week": 10, "day": "Tuesday", "name": "Pull Day", "objective": "Explosiveness + real strength on pull-ups, high quality, low cognitive load", "exercise_prescriptions": [{"id": "w10_pull_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_description": "Shoulder and T-spine mobility", "target_duration_seconds": 180}, {"order": 2, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 1"}, {"order": 3, "target_reps": 8, "target_load": 0, "target_notes": "Scap pull-up light set 2"}, {"order": 4, "target_duration_seconds": 30, "target_notes": "Active dead hang set 1"}, {"order": 5, "target_duration_seconds": 30, "target_notes": "Active dead hang set 2"}]}, {"id": "w10_pull_emom", "exercise_name": "Explosive Pull-up EMOM", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 1, "target_load": 0, "target_notes": "Minute 1"}, {"order": 2, "target_reps": 2, "target_load": 0, "target_notes": "Minute 2"}, {"order": 3, "target_reps": 1, "target_load": 0, "target_notes": "Minute 3"}, {"order": 4, "target_reps": 2, "target_load": 0, "target_notes": "Minute 4"}, {"order": 5, "target_reps": 1, "target_load": 0, "target_notes": "Minute 5"}, {"order": 6, "target_reps": 2, "target_load": 0, "target_notes": "Minute 6"}]}, {"id": "w10_pull_main", "exercise_name": "Weighted Pull-up with Stops", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 6, "target_load": 2, "target_percentage": 78, "rest_after_set_seconds": 150, "target_notes": "Week 10 progression"}, {"order": 2, "target_reps": 4, "target_load": 8, "target_percentage": 84, "rest_after_set_seconds": 150, "target_notes": "Week 10 progression"}, {"order": 3, "target_reps": 2, "target_load": 13, "target_percentage": 90, "rest_after_set_seconds": 180, "target_notes": "Week 10 progression"}, {"order": 4, "target_reps": 1, "target_load": 23, "target_percentage": 100, "rest_after_set_seconds": 180, "target_notes": "Week 10 progression"}]}, {"id": "w10_pull_accessory", "exercise_name": "Neutral Grip Lat Machine", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 2, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}, {"order": 3, "target_reps_range": "technical failure", "target_load": null, "rest_after_set_seconds": 90, "target_notes": "Active scap pause before set"}]}, {"id": "w10_pull_military", "exercise_name": "Dumbbell Military Press", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 6, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w10_pull_biceps", "exercise_name": "Cable Curl", "type": "accessory", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 60}]}, {"id": "w10_pull_core", "exercise_name": "Plank", "type": "core", "inter_exercise_rest_seconds": 0, "set_blocks": [{"order": 1, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 2, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 3, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 4, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 5, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 6, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 7, "target_duration_seconds": 30, "rest_after_set_seconds": 30}, {"order": 8, "target_duration_seconds": 30, "rest_after_set_seconds": 30}]}]}, {"id": "week10_push", "week": 10, "day": "Thursday", "name": "Push Day", "objective": "Real dip strength, sustainable pushing volume, protected shoulders and elbows", "exercise_prescriptions": [{"id": "w10_push_warmup", "exercise_name": "General Warm-up", "type": "warmup", "inter_exercise_rest_seconds": 60, "set_blocks": [{"order": 1, "target_duration_seconds": 600, "target_notes": "Bike"}, {"order": 2, "target_duration_seconds": 180, "target_notes": "Push-up + Jumping Jack"}]}, {"id": "w10_push_simil_mu", "exercise_name": "Simil Muscle-Up", "type": "technique", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 0, "target_notes": "Slow and controlled, 1s pause on top"}]}, {"id": "w10_push_main", "exercise_name": "Weighted Dip", "type": "main_lift", "inter_exercise_rest_seconds": 120, "set_blocks": [{"order": 1, "target_reps": 6, "target_load": 12, "target_percentage": 78, "rest_after_set_seconds": 150, "target_notes": "Week 10 progression"}, {"order": 2, "target_reps": 4, "target_load": 18, "target_percentage": 84, "rest_after_set_seconds": 150, "target_notes": "Week 10 progression"}, {"order": 3, "target_reps": 2, "target_load": 25, "target_percentage": 90, "rest_after_set_seconds": 180, "target_notes": "Week 10 progression"}, {"order": 4, "target_reps": 1, "target_load": 35, "target_percentage": 100, "rest_after_set_seconds": 180, "target_notes": "Week 10 progression"}]}, {"id": "w10_push_complementary", "exercise_name": "Dumbbell Bench Press", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}, {"order": 4, "target_reps": 10, "target_load": null, "rest_after_set_seconds": 90}]}, {"id": "w10_push_bardip", "exercise_name": "Bar Dip", "type": "accessory", "inter_exercise_rest_seconds": 90, "set_blocks": [{"order": 1, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 2, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}, {"order": 3, "target_reps": 8, "target_load": 10, "rest_after_set_seconds": 90}]}, {"id": "w10_push_core", "exercise_name": "Sit-up", "type": "core", "inter_exercise_rest_seconds": 30, "set_blocks": [{"order": 1, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 2, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}, {"order": 3, "target_reps": 15, "target_load": 0, "rest_after_set_seconds": 30}]}]}]}, "planned_sessions": [{"id": "session_2025_12_10_week1_pull", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week1_pull", "scheduled_date": "2025-12-10", "status": "completed"}, {"id": "session_2025_12_12_week1_push", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week1_push", "scheduled_date": "2025-12-12", "status": "completed"}, {"id": "session_2025_12_17_week2_pull", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week2_pull", "scheduled_date": "2025-12-17", "status": "completed"}, {"id": "session_2025_12_19_week2_push", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week2_push", "scheduled_date": "2025-12-19", "status": "completed"}, {"id": "session_2025_12_24_week3_pull", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week3_pull", "scheduled_date": "2025-12-24", "status": "completed"}, {"id": "session_2025_12_26_week3_push", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week3_push", "scheduled_date": "2025-12-26", "status": "completed"}, {"id": "session_2025_12_31_week4_pull", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week4_pull", "scheduled_date": "2025-12-31", "status": "completed"}, {"id": "session_2026_01_02_week4_push", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week4_push", "scheduled_date": "2026-01-02", "status": "completed"}, {"id": "session_2026_01_07_week5_pull", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week5_pull", "scheduled_date": "2026-01-07", "status": "completed"}, {"id": "session_2026_01_09_week5_push", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week5_push", "scheduled_date": "2026-01-09", "status": "completed"}, {"id": "session_2026_01_14_week6_pull", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week6_pull", "scheduled_date": "2026-01-14", "status": "completed"}, {"id": "session_2026_01_16_week6_push", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week6_push", "scheduled_date": "2026-01-16", "status": "completed"}, {"id": "session_2026_01_21_week7_pull", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week7_pull", "scheduled_date": "2026-01-21", "status": "completed"}, {"id": "session_2026_01_23_week7_push", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week7_push", "scheduled_date": "2026-01-23", "status": "completed"}, {"id": "session_2026_01_28_week8_pull", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week8_pull", "scheduled_date": "2026-01-28", "status": "planned"}, {"id": "session_2026_01_30_week8_push", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week8_push", "scheduled_date": "2026-01-30", "status": "planned"}, {"id": "session_2026_02_04_week9_pull", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week9_pull", "scheduled_date": "2026-02-04", "status": "planned"}, {"id": "session_2026_02_06_week9_push", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week9_push", "scheduled_date": "2026-02-06", "status": "planned"}, {"id": "session_2026_02_11_week10_pull", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week10_pull", "scheduled_date": "2026-02-11", "status": "planned"}, {"id": "session_2026_02_13_week10_push", "athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "workout_id": "week10_push", "scheduled_date": "2026-02-13", "status": "planned"}], "training_state": {"athlete_id": "ath_angelo_001", "program_id": "prog_winter_001", "current_week": 8, "current_workout_id": "week8_pull", "current_status": "planned", "last_completed_workout_id": "week7_push", "missed_workouts_count": 0}}`);

/* F2 augmentation table — applied at normalization time per exercise_name.
 * priority: main / secondary / optional   (F1-AM-7)
 * neuro_type: neural / hypertrophy / accessory / core   (F1-AM-8 §7)
 * goal: optional per-exercise override   (DL-2 hybrid)
 *
 * Tweak this table to remap rule-engine behaviour without touching the F1 sample. */
const F2_AUGMENT = {
  // Warm-ups + core
  "General Warm-up":          { neuro_type: "core",        priority: "main" },
  "Plank":                    { neuro_type: "core",        priority: "main" },
  "Sit-up":                   { neuro_type: "core",        priority: "main" },
  // Technique — neural
  "Explosive Pull-up EMOM":   { neuro_type: "neural",      priority: "main" },
  "Simil Muscle-Up":          { neuro_type: "neural",      priority: "main" },
  // Main lifts — neural + strength override
  "Weighted Pull-up with Stops": { neuro_type: "neural",   priority: "main", goal: "strength" },
  "Weighted Dip":             { neuro_type: "neural",      priority: "main", goal: "strength" },
  // Accessories — hypertrophy
  "Neutral Grip Lat Machine": { neuro_type: "hypertrophy", priority: "secondary" },
  "Dumbbell Military Press":  { neuro_type: "hypertrophy", priority: "secondary" },
  /* Push Day chain note: Simil-MU(N) → Weighted Dip(N) → Bench(N) → Bar Dip(N).
     Both Bench Press and Bar Dip are kept on neuro_type=neural — at street-lifting
     loads they're CNS-demanding compounds. Result: §5 neural stack fires when
     entering Bench Press (3rd consecutive neural). Flip either to hypertrophy
     to disable §5 on Push Day for validation purposes. */
  "Dumbbell Bench Press":     { neuro_type: "neural",      priority: "secondary" },
  "Bar Dip":                  { neuro_type: "neural",      priority: "secondary" },
  // The textbook skippable: Cable Curl → priority=optional so §4 auto-skip is reachable
  "Cable Curl":               { neuro_type: "hypertrophy", priority: "optional" },
};

const DAY_IT = {
  Monday:    "Lunedì",    Tuesday:  "Martedì",
  Wednesday: "Mercoledì", Thursday: "Giovedì",
  Friday:    "Venerdì",   Saturday: "Sabato",
  Sunday:    "Domenica",
};

/* ───────────────────────────────────────────────────────────────────────────
   F0004 EXERCISE DEFAULTS — auto-default tagging per DL-25.
   Source of truth: product/features/FEATURE-0004-.../data/exercise-defaults.yml
   Embedded here for self-contained prototype operation. Keep the two in sync.
   ─────────────────────────────────────────────────────────────────────────── */
const F0004_DEFAULTS = {
  "General Warm-up":             { movement_pattern: "core", stimulus_type: "conditioning", joint_load: [],                          excluded_from_volume: true },
  "Explosive Pull-up EMOM":      { movement_pattern: "pull", stimulus_type: "max_strength", joint_load: ["shoulder","elbow"] },
  "Weighted Pull-up with Stops": { movement_pattern: "pull", stimulus_type: "max_strength", joint_load: ["shoulder","elbow"] },
  "Neutral Grip Lat Machine":    { movement_pattern: "pull", stimulus_type: "hypertrophy",  joint_load: ["shoulder","elbow"] },
  "Dumbbell Military Press":     { movement_pattern: "push", stimulus_type: "hypertrophy",  joint_load: ["shoulder","elbow","wrist"] },
  "Cable Curl":                  { movement_pattern: "pull", stimulus_type: "hypertrophy",  joint_load: ["elbow"] },
  "Plank":                       { movement_pattern: "core", stimulus_type: "conditioning", joint_load: ["shoulder","lower_back"] },
  "Simil Muscle-Up":             { movement_pattern: "pull", stimulus_type: "max_strength", joint_load: ["wrist","elbow","shoulder"] },
  "Weighted Dip":                { movement_pattern: "push", stimulus_type: "max_strength", joint_load: ["shoulder","elbow","wrist"] },
  "Dumbbell Bench Press":        { movement_pattern: "push", stimulus_type: "hypertrophy",  joint_load: ["shoulder","elbow","wrist"] },
  "Bar Dip":                     { movement_pattern: "push", stimulus_type: "hypertrophy",  joint_load: ["shoulder","elbow","wrist"] },
  "Sit-up":                      { movement_pattern: "core", stimulus_type: "hypertrophy",  joint_load: ["lower_back"] },
};

/* Fallback inference when a name isn't in F0004_DEFAULTS (per exercise-defaults.yml
   "inference_heuristics"). Order matters — first match wins. */
function inferF0004Defaults(name, category) {
  const n = (name || "").toLowerCase();
  // movement_pattern
  let mp = "core";
  if (/(front lever|planche|manna|handstand|iron cross|victorian|maltese)/.test(n)) mp = "skill";
  else if (/(deadlift|rdl|hip thrust|glute bridge|swing|good morning)/.test(n))    mp = "hinge";
  else if (/(squat|lunge|pistol|shrimp|leg press|split squat)/.test(n))            mp = "squat";
  else if (/(plank|sit-up|situp|hollow|dragon flag|l-sit|ab wheel)/.test(n))       mp = "core";
  else if (/(press|dip|push-?up|hspu|bench|ohp)/.test(n))                          mp = "push";
  else if (/(pull-?up|chin-?up|row|lat|curl|muscle-?up)/.test(n))                  mp = "pull";
  // stimulus_type
  let st = "hypertrophy";
  if (mp === "skill")              st = "skill";
  else if (category === "warmup")  st = "conditioning";
  // joint_load — conservative defaults from name keywords
  const joints = new Set();
  if (/(dip|push-?up|hspu|handstand|muscle-?up|planche|bench)/.test(n))    joints.add("wrist");
  if (/(pull-?up|dip|curl|press|push-?up|muscle-?up|chin-?up|bench|row)/.test(n)) joints.add("elbow");
  if (/(press|pull-?up|dip|lat|row|muscle-?up|planche|front lever|handstand)/.test(n)) joints.add("shoulder");
  if (/(deadlift|sit-?up|hollow|dragon flag|hip thrust)/.test(n))         joints.add("lower_back");
  return { movement_pattern: mp, stimulus_type: st, joint_load: Array.from(joints) };
}

/* normalizeSample(raw) — converts the F1 sample.json shape into the prototype's
 * internal PROGRAM shape, applying F2 + F0004 augmentations on the way through. */
function normalizeSample(raw) {
  const program = raw.program;
  const out = {
    name: program.name,
    athlete: raw.athlete && raw.athlete.name,
    duration_weeks: program.duration_weeks,
    current_week: program.current_week || 1,
    goal: program.goal || "hypertrophy",
    workouts: [],
  };
  for (const w of (program.workouts || [])) {
    out.workouts.push({
      id: w.id,
      week: w.week,
      day: DAY_IT[w.day] || w.day,
      name: w.name,
      goal: w.goal,
      exercises: (w.exercise_prescriptions || []).map(ex => {
        const aug = F2_AUGMENT[ex.exercise_name] || {};
        // Default neuro_type per category if no augmentation is provided
        const defaultNeuro = ({
          warmup: "core", technique: "neural", main_lift: "neural",
          accessory: "hypertrophy", core: "core",
        })[ex.type] || "core";
        // F0004 tags — explicit table, fallback to inference (DL-25)
        const f4 = F0004_DEFAULTS[ex.exercise_name]
                || inferF0004Defaults(ex.exercise_name, ex.type);
        const e = {
          id: ex.id,
          name: ex.exercise_name,
          category:   ex.type,                              // F1-AM-8 rename
          neuro_type: aug.neuro_type || defaultNeuro,       // F1-AM-8 + DL-10  (deprecated by F0004 §5/DL-24, kept for legacy paths)
          priority:   aug.priority   || "main",             // F1-AM-7 (default main per DL-13)
          inter_rest: ex.inter_exercise_rest_seconds || 0,
          sets: (ex.set_blocks || []).map(s => normalizeSet(s)),
          // F0004 program-data amendments F1-AM-9..11
          movement_pattern:     f4.movement_pattern,
          stimulus_type:        f4.stimulus_type,
          joint_load:           f4.joint_load || [],
          excluded_from_volume: !!f4.excluded_from_volume,
        };
        if (aug.goal) e.goal = aug.goal;                    // optional per-exercise override (DL-2)
        return e;
      }),
    });
  }
  return out;
}

function normalizeSet(s) {
  const base = {
    order: s.order,
    rest:  s.rest_after_set_seconds || 0,
    notes: s.target_description || s.target_notes || undefined,
  };
  if (s.target_reps !== undefined) {
    return Object.assign(base, { kind: "reps", reps: s.target_reps, load: s.target_load === undefined ? null : s.target_load });
  }
  if (s.target_duration_seconds !== undefined) {
    return Object.assign(base, { kind: "duration", duration: s.target_duration_seconds });
  }
  if (s.target_reps_range !== undefined) {
    return Object.assign(base, { kind: "open", range: s.target_reps_range, load: s.target_load === undefined ? null : s.target_load });
  }
  // Fallback: treat as duration 30s if nothing recognisable
  return Object.assign(base, { kind: "duration", duration: 30 });
}

// Sprint 2.2 (2026-05-29): PROGRAM cambiato da const a let. Inizializzato col
// sample.json embed come fallback durante splash + per utenti non autenticati;
// rimpiazzato da loadProgramFromDB() quando l'atleta logga (vedi sezione 12 auth).
let PROGRAM = normalizeSample(RAW_SAMPLE);

/* ------------------------- 2. STATE ------------------------- */
const state = {
  // selection
  selectedWeek: 1,
  selectedWorkoutId: null,
  // session
  workout: null,
  exIdx: 0,
  setIdx: 0,
  preloadedNullLoad: null,         // load chosen on §preload screen for current ex
  loadOverrides: {},                // { exId+'#'+order: actualLoad }
  setLog: [],                       // [{ exId, order, kind, target, actual, load, signal, effort, tech, fired:[], skipped }]
  // rule-engine bookkeeping
  perExerciseLogs: {},              // { exId: [setLogEntries] }
  cumulativeFiredFor: new Set(),    // exIds where §3 already fired (idempotent)
  severeFiredFor: new Set(),        // exIds where §2 fired (suppresses F1 STORY-014)
  startedExerciseSequence: [],      // ordered list of exIds started in this session
  pendingDispatcherMessage: null,   // single message to surface on the *next* set card
  pendingRestExtraSeconds: 0,       // extra rest queued for the upcoming rest period
  pendingAutoSkipFor: null,         // exId targeted by §4 auto-skip
  perSetCapture: {},                // captured (effort, tech) per set, key = exId#order
  // F0004 PS-1 — running session counters of "hard sets" per movement_pattern.
  // For v7.0 prototype scope: only this-session counters (no cross-session history yet).
  // History layer (rolling 7/14/28-day) lands in v7.x once we have multi-session data.
  weeklyVolume: {
    thisSession: { push: 0, pull: 0, squat: 0, hinge: 0, core: 0, skill: 0 },
    // v7.2 — last band label observed per pattern (used to detect crossings)
    lastBand:    { push: 'below_mev', pull: 'below_mev', squat: 'below_mev',
                   hinge: 'below_mev', core: 'below_mev', skill: 'n/a' },
    // v7.2 — flags so each crossing dispatches at most once per session per pattern
    firedCaps:   { push: {}, pull: {}, squat: {}, hinge: {}, core: {}, skill: {} },
  },
  // F0004 PS-3 — last-resolved inter-exercise rest decision (for inspector visibility)
  lastInterExRestDecision: null,
  // F0004 PS-4c — joint readiness state, populated on screen-checkin.
  // Default null = no problem reported on that joint.
  jointReadiness: { wrist: null, elbow: null, shoulder: null, lower_back: null },
  inspector: {
    log: [],
    rulesFired: {},                 // { ruleId: count }
    /* STORY-027 M-3 — athlete-facing message volume per session */
    messageCount: { total: 0, byCode: {} },
    /* STORY-028/030 event ledger for post-workout summary + coach report */
    events: [],                     // { kind, exId, exName, code, msg, t, meta }
  },
  rest: {
    timerId: null,
    secondsLeft: 0,
    totalSeconds: 0,
    onComplete: null,
  },
  trans: { timerId: null },
  autoSkip: { timerId: null, secondsLeft: 5, onConfirm: null },
};

/* ------------------------- 3. UTILS ------------------------- */
const C = 2 * Math.PI * 96; // ring circumference
function $(id) { return document.getElementById(id); }
function fmtMMSS(s) { const m = Math.floor(s/60); const ss = String(s%60).padStart(2,'0'); return m+':'+ss; }
function fmtLoad(load) {
  if (load === null || load === undefined) return "—";
  if (load === 0) return "Corpo libero";
  return "+" + (Number.isInteger(load) ? load : load.toFixed(1)) + " kg";
}
function roundHalf(n) { return Math.round(n * 2) / 2; }
function categoryBadgeClass(cat){
  return ({warmup:'warm',technique:'tech',main_lift:'main',accessory:'acc',core:'core'})[cat]||'sec';
}
function priorityBadgeClass(p){
  return ({main:'main',secondary:'sec',optional:'opt'})[p]||'sec';
}
function neuroBadgeClass(n){
  return ({neural:'neural',hypertrophy:'hyp',accessory:'acc',core:'core'})[n]||'sec';
}
function show(screenId, prevId) {
  const target = $('screen-'+screenId);
  const all = document.querySelectorAll('.screen');
  all.forEach(s => s.classList.remove('active','exit'));
  target.classList.add('active');
}

/* ------------------------- 4. RULE ENGINE ------------------------- */

/* Magnitudes per goal — DL-3
 * Sprint 2.2 (2026-05-29): aggiunto "mixed" per supportare programs.goal='mixed'
 * (Lynar nuova categoria, non in F1/F2 original). Valori intermedi tra hypertrophy
 * e strength — fungono da default ragionevole finche' il rule engine non avra'
 * un trattamento dedicato per block-style programs.
 * TODO Sprint 3+: rivisitare valori "mixed" basati sui dati reali di adaptation
 * (al momento half-way arbitrario). */
const MAG = {
  hypertrophy: { load_pct: 0.05,  rep: 1, rest_s: 30 },
  strength:    { load_pct: 0.10,  rep: 1, rest_s: 90 },  // mid of 60-120
  mixed:       { load_pct: 0.075, rep: 1, rest_s: 60 },  // halfway between
};
const SEVERE_REDUCTION = 0.30; // DL-8, goal-independent

/* ───────────────────────────────────────────────────────────────────────────
   F0004 PS-1 — Weekly volume bands per movement_pattern.
   Source: Israetel RP "Scientific Principles of Hypertrophy Training" volume
   landmarks, cross-referenced with Helms MASS reviews (PRD OQ-27 working
   assumption). Numbers are HARD SETS per pattern per 7 days.
   ─────────────────────────────────────────────────────────────────────────── */
const VOLUME_BANDS = {
  push:  { mev:  8, mav: 18, mrv: 22 },  // chest+triceps+anterior delt aggregate
  pull:  { mev: 10, mav: 20, mrv: 25 },  // lat+biceps aggregate, traditionally tolerates more
  squat: { mev:  8, mav: 16, mrv: 22 },
  hinge: { mev:  6, mav: 14, mrv: 18 },
  core:  { mev:  0, mav: 16, mrv: 25 },  // very flexible, low systemic cost
  skill: { mev:  0, mav:  0, mrv:  0 },  // skill is TUT-driven, not set-driven (PS-4b later)
};

/* F0004 PS-3 — Inter-exercise rest minimums per stimulus_type. Compresses the
   PS-3 recovery-window table into a between-exercises floor. */
const STIMULUS_REST_FLOOR = {
  max_strength: 150,   // bumped to 180 when goal=strength (see resolveInterExRest)
  hypertrophy:   90,
  skill:         90,
  conditioning:  60,
};

/* Resolve goal — DL-2 hybrid (program → workout → exercise override) */
function resolveGoal(exercise, workout, program) {
  if (exercise.goal) return exercise.goal;
  if (workout && workout.goal) return workout.goal;
  return program.goal || 'hypertrophy';
}

/* F-8 Sprint 2.2 (2026-06-10): exerciseIsBodyweight(ex)
 * Detect se un esercizio e' a corpo libero (tutti i set reps-kind hanno load=0|null).
 * Usato dalle 3 site di dispatch del rule engine per evitare messaggi user-facing
 * tipo "riduco il carico 30%" su esercizi senza carico (signal misleading, friction F-8
 * emerso nel field test dogfood 2026-05-29 + 2026-06-10).
 *
 * Logica: per essere considerato BW serve almeno 1 set reps-kind, e TUTTI devono
 * avere load 0 o null. Esercizi solo-duration (es. plank, hold) non sono BW in
 * questo senso (la regola §2/F1-STORY-014 non si applica a duration sets comunque). */
function exerciseIsBodyweight(ex) {
  const repsSets = ex.sets.filter(s => s.kind === 'reps');
  if (repsSets.length === 0) return false;
  return repsSets.every(s => s.load === 0 || s.load === null);
}

/* Map (effort, technique) → internal signal — §Internal Signal Mapping + DL-9 fallback */
function mapSignal(effort, technique) {
  if (effort === 'fail') return { signal: 'overload', fallback: false };
  const k = effort + '+' + technique;
  const lookup = {
    'easy+clean'  : 'underload',
    'ok+clean'    : 'optimal',
    'hard+clean'  : 'managed_fatigue',
    'hard+dirty'  : 'neural_fatigue',
  };
  if (lookup[k]) return { signal: lookup[k], fallback: false };
  // DL-9 fallback (easy+dirty, ok+dirty)
  return { signal: 'managed_fatigue', fallback: true };
}

/* §1 Main exercise rules — produce action object */
function mainRule(signal, goal, neuralStackActive) {
  const mag = MAG[goal];
  if (signal === 'underload')
    return { action: 'increase', load_delta: +mag.load_pct, rep_delta: +mag.rep, rest_delta: 0,
             msg: 'Aumenta carico al prossimo set', code: '§1 underload' };
  if (signal === 'optimal')
    return { action: 'keep', load_delta: 0, rep_delta: 0, rest_delta: 0,
             msg: 'Mantieni così', code: '§1 optimal' };
  if (signal === 'managed_fatigue')
    return { action: 'keep', load_delta: 0, rep_delta: 0, rest_delta: 0,
             flag: 'fatigue', msg: 'Mantieni, gestisci la fatica', code: '§1 managed_fatigue' };
  if (signal === 'neural_fatigue') {
    // Under active neural stack (§5/DL-14), downgrade tolerance: produce reduce instead of keep+flag
    if (neuralStackActive)
      return { action: 'reduce', load_delta: -mag.load_pct, rep_delta: 0, rest_delta: +mag.rest_s,
               msg: 'Carico ridotto del 5%. Recupera bene.', code: '§5+§1 neural_fatigue (stack-active)' };
    return { action: 'increase_rest', load_delta: 0, rep_delta: 0, rest_delta: +mag.rest_s,
             msg: 'Aumento il riposo', code: '§1 neural_fatigue' };
  }
  if (signal === 'overload')
    return { action: 'reduce', load_delta: -mag.load_pct, rep_delta: -mag.rep, rest_delta: +mag.rest_s,
             msg: 'Riduco carico per proteggere il volume', code: '§1 overload' };
  return null;
}

/* §3 Cumulative fatigue — exact 3-set sliding window per exercise (DL-12) */
function checkCumulativeFatigue(exId, perExLog) {
  if (state.cumulativeFiredFor.has(exId)) return null;
  if (perExLog.length < 3) return null;
  const last3 = perExLog.slice(-3);
  const efforts = last3.map(s => s.effort).join(',');
  const techs   = last3.map(s => s.tech).join(',');
  // Effort progression ok→hard→fail OR technique progression clean→dirty→fail
  const effortMatch = efforts === 'ok,hard,fail';
  const techMatch   = techs   === 'clean,dirty,fail';
  if (effortMatch || techMatch) return { match: effortMatch ? 'effort' : 'tech', code: '§3 cumulative_fatigue' };
  return null;
}

/* §5 Neural stack count — count consecutive starts of neuro_type==neural */
function neuralStackActive(seq) {
  let count = 0;
  for (let i = seq.length - 1; i >= 0; i--) {
    const ex = findExercise(seq[i]);
    if (ex && ex.neuro_type === 'neural') count++;
    else break;
  }
  return count >= 3;
}
function neuralStackCountAtStartOf(exId) {
  // count consecutive neural exercises ending with this one
  const seq = state.startedExerciseSequence;
  let count = 0;
  for (let i = seq.length - 1; i >= 0; i--) {
    const ex = findExercise(seq[i]);
    if (ex && ex.neuro_type === 'neural') count++;
    else break;
  }
  return count;
}

/* §6 Inter-exercise rest — F0004 PS-3: stimulus-typed minimums replace the
   binary "neural / not neural" heuristic from F0002 §5/§6. Coach prescription
   is still honored when generous enough (≥120s, DL-11 unchanged). */
function resolveInterExRest(programmedSeconds, prevStimulus, nextStimulus, goal) {
  // Honor coach when their prescription is already generous
  if (programmedSeconds >= 120) return programmedSeconds;
  // Stimulus-typed floor — take the max between prev and next stimulus floors
  const prevFloor = STIMULUS_REST_FLOOR[prevStimulus] || 60;
  const nextFloor = STIMULUS_REST_FLOOR[nextStimulus] || 60;
  let floor = Math.max(prevFloor, nextFloor);
  // Goal=strength bumps max_strength from 150 → 180 (top of the 60-120 PRD band)
  if (goal === 'strength' && (prevStimulus === 'max_strength' || nextStimulus === 'max_strength')) {
    floor = Math.max(floor, 180);
  }
  return Math.max(programmedSeconds, floor);
}

/* Apply load delta — rounded to 0.5 kg, skip bodyweight + null */
function applyLoadDelta(load, deltaPct) {
  if (load === null || load === undefined || load === 0) return load;
  return roundHalf(load * (1 + deltaPct));
}
function applySevere(load) {
  if (load === null || load === undefined || load === 0) return load;
  return roundHalf(load * (1 - SEVERE_REDUCTION));
}

/* ───────────────────────────────────────────────────────────────────────────
   F0004 PS-1 — Volume aggregator helpers. Silent in v7.0 (inspector-only).
   ─────────────────────────────────────────────────────────────────────────── */

/* "Hard set" definition for v7.0 — kept simple while OQ-32 (effort fill-rate
   root cause) is open. Heuristic:
     · cedimento path → always counts (athlete signaled they hit the wall)
     · prescribed path on max_strength or hypertrophy → counts as 1 hard set
     · skill / conditioning → does not count toward push/pull/squat/hinge volume
     · excluded_from_volume exercises (warmups) → do not count
   When PS-2 RPE opt-in lands, this becomes "RPE ≥ 7 OR cedimento". */
function isHardSet(setLogEntry) {
  const ex = findExercise(setLogEntry.exId);
  if (!ex) return false;
  if (ex.excluded_from_volume) return false;
  if (ex.stimulus_type === 'conditioning' || ex.stimulus_type === 'skill') return false;
  if (setLogEntry.fatigue_path) return true;
  return setLogEntry.kind === 'reps' || setLogEntry.kind === 'open';
}

function recordHardSetIfApplicable(entry) {
  if (!isHardSet(entry)) return;
  const ex = findExercise(entry.exId);
  if (!ex || !ex.movement_pattern) return;
  const counters = state.weeklyVolume.thisSession;
  const pattern = ex.movement_pattern;
  if (counters[pattern] === undefined) counters[pattern] = 0;
  counters[pattern] += 1;
  inspectorLog('rule', { tag: 'PS-1 hard_set logged', code: 'PS-1 hard_set',
    msg: ex.name + ' → +1 hard set on ' + pattern,
    meta: 'session counter ' + pattern + '=' + counters[pattern] +
          ' (' + getVolumeBandLabel(pattern) + ')' });
  // v7.2 — detect band crossing and fire the volume_cap_per_pattern rule (DL-24
  // replacement for §5 neural stack). Each crossing fires at most once per session.
  evaluateVolumeCap(pattern, ex);
}

/* v7.2 — Volume cap rule (PS-5 Level 1, reading PS-1 context).
   Replaces the F0002 §5 neural stack heuristic per F0004 DL-24. */
const PATTERN_LABEL_IT = {
  push: 'spinte', pull: 'tirate', squat: 'squat',
  hinge: 'hip hinge', core: 'core', skill: 'skill',
};
function evaluateVolumeCap(pattern, currentEx) {
  const newBand = getVolumeBandLabel(pattern);
  const oldBand = state.weeklyVolume.lastBand[pattern] || 'below_mev';
  if (newBand === oldBand) return;
  state.weeklyVolume.lastBand[pattern] = newBand;
  // Only act on crossings INTO the warning zones; ignore reverse transitions.
  const fired = state.weeklyVolume.firedCaps[pattern];
  const lbl = PATTERN_LABEL_IT[pattern] || pattern;
  if (newBand === 'mav_to_mrv' && !fired.mav) {
    fired.mav = true;
    dispatch('Tante ' + lbl + ' oggi. Tieni la qualità.', 'info',
             { code: 'PS-1 volume_cap mav' });
    state.inspector.events.push({
      kind: 'volume_cap', t: Date.now(),
      meta: { pattern, band: newBand, count: state.weeklyVolume.thisSession[pattern] }
    });
    inspectorLog('rule', { tag: 'PS-1 volume_cap (mav)', code: 'PS-1 volume_cap',
      msg: pattern + ' entered MAV→MRV band — tieni la qualità',
      meta: 'count=' + state.weeklyVolume.thisSession[pattern] +
            ' bands MEV/MAV/MRV=' + JSON.stringify(VOLUME_BANDS[pattern]) });
  } else if (newBand === 'above_mrv' && !fired.mrv) {
    fired.mrv = true;
    // Apply -5% load to remaining sets of THIS exercise on this pattern.
    let reduced = 0;
    if (currentEx && currentEx.movement_pattern === pattern) {
      const remaining = currentEx.sets.filter(s => s.order > (state.setIdx + 1));
      remaining.forEach(s => {
        if (s.kind === 'reps' && s.load !== null && s.load !== 0) {
          const k = currentEx.id + '#' + s.order;
          const cur = state.loadOverrides[k] ?? s.load;
          state.loadOverrides[k] = roundHalf(cur * 0.95);
          reduced += 1;
        }
      });
    }
    const msg = reduced > 0
      ? lbl.charAt(0).toUpperCase() + lbl.slice(1) + ' al limite. Riduco il carico del 5%.'
      : lbl.charAt(0).toUpperCase() + lbl.slice(1) + ' al limite. Resta sul controllato.';
    dispatch(msg, 'warn', { code: 'PS-1 volume_cap mrv' });
    state.inspector.events.push({
      kind: 'volume_cap', t: Date.now(),
      meta: { pattern, band: newBand, count: state.weeklyVolume.thisSession[pattern], reduced }
    });
    inspectorLog('rule', { tag: 'PS-1 volume_cap (above mrv)', code: 'PS-1 volume_cap',
      msg: pattern + ' above MRV — load -5% applied to ' + reduced + ' remaining set(s)',
      meta: 'DL-24: replaces §5 neural stack with volume-driven cap' });
  }
}

/* Returns the band the current per-pattern session counter sits in.
   Bands compare the session counter directly to weekly priors — i.e. this is
   "if you maintain today's rate for the week, where would you land". A multi-
   session history layer lands in v7.x. */
function getVolumeBandLabel(pattern) {
  const v = (state.weeklyVolume.thisSession[pattern] || 0);
  const b = VOLUME_BANDS[pattern] || { mev: 0, mav: 0, mrv: 0 };
  if (b.mrv === 0) return 'n/a';                // skill etc. — not set-based
  if (v < b.mev) return 'below_mev';
  if (v < b.mav) return 'mev_to_mav';
  if (v < b.mrv) return 'mav_to_mrv';
  return 'above_mrv';
}

/* §8 Dispatcher — single source of status copy. Stored, surfaced once on the next set card */
/* FEATURE-0003 STORY-024: opts.silent suppresses athlete-facing toast (silent log only). */
/* FEATURE-0003 STORY-027: every non-silent dispatch increments M-3 counters (total + per-code). */
const SILENT_RULE_CODES = new Set([
  '§1 underload',
  '§1 optimal',
  '§1 managed_fatigue',
  '§1 neural_fatigue',
  '§1 overload',
]);
function dispatch(message, severity = 'info', opts = {}) {
  const silent = opts.silent === true;
  const code = opts.code || null;
  if (silent) return; // STORY-024: §1 family is silent log only — inspectorLog still records the firing
  state.pendingDispatcherMessage = { msg: message, severity };
  // STORY-027 M-3 instrumentation
  state.inspector.messageCount.total += 1;
  if (code) {
    state.inspector.messageCount.byCode[code] = (state.inspector.messageCount.byCode[code]||0) + 1;
  }
}

function findExercise(exId) {
  if (!state.workout) return null;
  return state.workout.exercises.find(e => e.id === exId);
}

/* Inspector logging */
function inspectorLog(kind, payload) {
  state.inspector.log.unshift({ t: Date.now(), kind, ...payload });
  if (state.inspector.log.length > 200) state.inspector.log.pop();
  if (payload.code) state.inspector.rulesFired[payload.code] = (state.inspector.rulesFired[payload.code]||0) + 1;
  renderInspector();
}

/* ===== Core engine entry — called after each set is logged ===== */
function evaluateRulesAfterSet(setLogEntry) {
  const ex = findExercise(setLogEntry.exId);
  if (!ex) return;
  const workout = state.workout;
  const goal = resolveGoal(ex, workout, PROGRAM);
  const inScopeForCompletion = ['main_lift','accessory'].includes(ex.category);
  const stackActive = neuralStackActive(state.startedExerciseSequence);

  // Push to per-exercise log FIRST — before any rule that may early-return.
  // §3 cumulative fatigue requires set 3 to be present in the history even when §2 also fires.
  const perEx = state.perExerciseLogs[ex.id] = state.perExerciseLogs[ex.id] || [];
  perEx.push(setLogEntry);

  // F0004 PS-1 — increment volume counters if this counts as a hard set
  recordHardSetIfApplicable(setLogEntry);

  inspectorLog('set', {
    tag: 'set logged', exId: ex.id, order: setLogEntry.order, goal,
    msg: ex.name + ' · set ' + setLogEntry.order,
    meta: 'effort=' + (setLogEntry.effort||'—') + ' tech=' + (setLogEntry.tech||'—') +
          ' actual=' + (setLogEntry.actual ?? '—') + ' target=' + (setLogEntry.target ?? '—') +
          ' load=' + (setLogEntry.load ?? '—'),
    code: null,
  });

  // ----- §2 Severe shortfall (rep-based, in-scope only). DL-4: precedes F1 STORY-014.
  if (setLogEntry.kind === 'reps' && inScopeForCompletion && setLogEntry.target > 0) {
    const ratio = setLogEntry.actual / setLogEntry.target;
    if (ratio < 0.5 && !state.severeFiredFor.has(ex.id)) {
      state.severeFiredFor.add(ex.id);
      // apply -30% to remaining sets in this exercise
      const remaining = ex.sets.filter(s => s.order > setLogEntry.order);
      remaining.forEach(s => {
        if (s.kind === 'reps' && s.load !== null && s.load !== 0) {
          const newLoad = applySevere(s.load);
          state.loadOverrides[ex.id+'#'+s.order] = newLoad;
        }
      });
      /* STORY-026 (FEATURE-0003): if there are no remaining sets, suppress the athlete-facing
         "-30% sui set rimanenti" message — there's nothing to reduce. The firing is still
         recorded in the inspector and feeds the coach report (STORY-030). OQ-17 default = no
         carry-forward; the event surfaces to the coach for next-cycle planning. */
      const isLastSet = remaining.length === 0;
      if (!isLastSet) {
        // F-8 (Sprint 2.2 fix 2026-06-10): BW exercises non hanno carico da ridurre.
        // Swap messaggio user-facing per essere onesti sul tipo di adaptation.
        if (exerciseIsBodyweight(ex)) {
          dispatch('Riduci il target reps sui set rimanenti per preservare qualità', 'warn',
                   { code: '§2 severe_shortfall (bw)' });
        } else {
          const exampleLoad = setLogEntry.load && setLogEntry.load !== 0
            ? '(' + setLogEntry.load + 'kg → ' + applySevere(setLogEntry.load) + 'kg)'
            : '';
          dispatch('Riduco il carico del 30% per mantenere qualità ' + exampleLoad, 'warn',
                   { code: '§2 severe_shortfall' });
        }
      }
      // STORY-028/030: ledger entry for post-workout summary + coach report
      state.inspector.events.push({
        kind: 'severe_shortfall', exId: ex.id, exName: ex.name,
        code: '§2 severe_shortfall', t: Date.now(),
        meta: { ratio, lastSet: isLastSet, loadBefore: setLogEntry.load,
                loadAfterExample: setLogEntry.load && setLogEntry.load !== 0
                  ? applySevere(setLogEntry.load) : null,
                actual: setLogEntry.actual, target: setLogEntry.target }
      });
      inspectorLog('rule', { tag: '§2 severe shortfall', code: '§2 severe_shortfall',
        msg: isLastSet
          ? 'ratio ' + ratio.toFixed(2) + ' < 0.5 → last set, no remaining sets to reduce'
          : 'ratio ' + ratio.toFixed(2) + ' < 0.5 → -30% sui set rimanenti',
        meta: 'F1 STORY-014 suppressed for this exercise (DL-4)' +
              (isLastSet ? ' (STORY-026: last-set silent)' : '') });
      return; // §2 takes precedence — skip F1 STORY-014 standard rep-shortfall path
    }
  }

  // ----- F1 STORY-014 standard rep shortfall (only if §2 didn't fire) -----
  if (setLogEntry.kind === 'reps' && inScopeForCompletion &&
      setLogEntry.target > 0 && setLogEntry.actual < setLogEntry.target &&
      !state.severeFiredFor.has(ex.id)) {
    const mag = MAG[goal];
    const remaining = ex.sets.filter(s => s.order > setLogEntry.order);
    remaining.forEach(s => {
      if (s.kind === 'reps' && s.load !== null && s.load !== 0) {
        const newLoad = applyLoadDelta(s.load, -mag.load_pct);
        state.loadOverrides[ex.id+'#'+s.order] = newLoad;
      }
    });
    // F-8 (Sprint 2.2 fix 2026-06-10): BW exercises non ricevono load adjustment.
    // Swap messaggio per non parlare di carico inesistente.
    if (exerciseIsBodyweight(ex)) {
      dispatch('Preserva qualità sui set rimanenti (corpo libero)', 'warn',
               { code: 'F1-STORY-014 (bw)' });
    } else {
      dispatch('Carico adattato (-' + (mag.load_pct*100) + '%) per i set rimanenti', 'warn',
               { code: 'F1-STORY-014' });
    }
    inspectorLog('rule', { tag: 'F1 STORY-014 (goal-aware)', code: 'F1-STORY-014',
      msg: 'reps shortfall — load_delta=-' + (mag.load_pct*100) + '% (' + goal + ')'
        + (exerciseIsBodyweight(ex) ? ' [BW: load_delta no-op, msg swapped]' : ''),
      meta: 'F1-AM-2 goal-aware magnitudes applied' });
  }

  // ----- §1 Main rules (only when capture present + main_lift/accessory rep sets) -----
  if (setLogEntry.effort && setLogEntry.tech && inScopeForCompletion) {
    const map = mapSignal(setLogEntry.effort, setLogEntry.tech);
    setLogEntry.signal = map.signal;
    if (map.fallback) {
      inspectorLog('rule', { tag: 'DL-9 fallback', code: 'DL-9 fallback',
        msg: 'unmapped pair (' + setLogEntry.effort + ',' + setLogEntry.tech + ') → managed_fatigue',
        meta: 'telemetry flag emitted' });
    }
    const action = mainRule(map.signal, goal, stackActive);
    if (action) {
      const isSilentFamily = SILENT_RULE_CODES.has(action.code); // STORY-024
      /* STORY-025: §1 underload/overload no longer mutate load — coach's program is authoritative.
         §5+§1 stack-active variant still applies load_delta (it's effectively §5, unaffected by FEATURE-0003). */
      const skipLoadDelta = isSilentFamily &&
        (action.code === '§1 underload' || action.code === '§1 overload');
      // apply action to remaining sets
      const remaining = ex.sets.filter(s => s.order > setLogEntry.order);
      if (action.load_delta !== 0 && !skipLoadDelta) {
        remaining.forEach(s => {
          if (s.kind === 'reps' && s.load !== null && s.load !== 0) {
            const cur = state.loadOverrides[ex.id+'#'+s.order] ?? s.load;
            const newLoad = applyLoadDelta(cur, action.load_delta);
            state.loadOverrides[ex.id+'#'+s.order] = newLoad;
          }
        });
      }
      /* STORY-024: §1 neural_fatigue keeps its rest_delta as silent side-effect (OQ-16 default = also silent on rest;
         we keep the rest bump because it's a useful protective signal even without a toast — re-evaluate per OQ-16). */
      if (action.rest_delta > 0 && !isSilentFamily) state.pendingRestExtraSeconds += action.rest_delta;
      const sev = action.action === 'reduce' ? 'warn' : 'info';
      // F-8 (Sprint 2.2 fix 2026-06-10): swap action.msg per BW exercises se l'azione
      // parla di carico. mainRule produce msg load-centric per underload/overload/neural-stack;
      // per BW questi messaggi sono misleading. Le altre azioni (optimal, managed_fatigue,
      // neural_fatigue increase_rest) sono gia' BW-safe.
      let displayMsg = action.msg;
      if (exerciseIsBodyweight(ex)) {
        if (action.code === '§1 underload') {
          displayMsg = 'Aumenta target reps al prossimo set';
        } else if (action.code === '§1 overload') {
          displayMsg = 'Mantieni reps, focus tecnica';
        } else if (action.code === '§5+§1 neural_fatigue (stack-active)') {
          displayMsg = 'Riduci reps prossimo set. Recupera bene.';
        }
      }
      dispatch(displayMsg, sev, { silent: isSilentFamily, code: action.code });
      /* STORY-030 ledger entry — captures every §1 firing with exId so the coach report
         can curate per OQ-18 (default: surface only those tied to an exercise where the
         athlete used the cedimento button). */
      state.inspector.events.push({
        kind: isSilentFamily ? 'rule_silent' : 'rule', exId: ex.id, exName: ex.name,
        code: action.code, t: Date.now(),
        meta: { silent: isSilentFamily, action: action.action, signal: map.signal }
      });
      inspectorLog('rule', { tag: action.code, code: action.code,
        msg: action.msg, meta: 'goal=' + goal +
        ' load_Δ=' + (skipLoadDelta ? 0 : action.load_delta) +
        ' rep_Δ=' + action.rep_delta +
        ' rest_Δ=+' + (isSilentFamily ? 0 : action.rest_delta) + 's' +
        (isSilentFamily ? ' (silent log)' : '') +
        (skipLoadDelta ? ' (load_Δ removed by STORY-025)' : '') });
    }
  }

  // ----- §3 Cumulative fatigue (per exercise, sliding 3-set window) -----
  // (perEx already populated at top of function — see comment near the push.)
  const cum = checkCumulativeFatigue(ex.id, perEx);
  if (cum) {
    state.cumulativeFiredFor.add(ex.id);
    /* STORY-029 (FEATURE-0003): input-degraded mode — if the matched window has null effort/tech,
       reword the athlete-facing copy to drop the "(effort)"/"(technique)" attribution and flag
       input_degraded=true so the coach knows the rule fired with a degraded input set. */
    const last3 = perEx.slice(-3);
    const inputDegraded = last3.every(s => !s.effort && !s.tech);
    const msg = inputDegraded
      ? 'Tre set in calo. Fermo qui i pesanti — passa a volume controllato.'
      : 'Tre set in calo. Fermo qui i pesanti — passa a volume controllato.';
    dispatch(msg, 'warn', { code: '§3 cumulative_fatigue' });
    state.inspector.events.push({
      kind: 'cumulative_fatigue', exId: ex.id, exName: ex.name,
      code: '§3 cumulative_fatigue', t: Date.now(),
      meta: { input_degraded: inputDegraded, match: cum.match }
    });
    inspectorLog('rule', { tag: '§3 cumulative fatigue', code: '§3 cumulative_fatigue',
      msg: inputDegraded
        ? 'progression matched on last 3 sets (input_degraded=true)'
        : 'progression matched (' + cum.match + ') over last 3 sets',
      meta: 'fired once per exercise per session (idempotent)' +
            (inputDegraded ? ' input_degraded=true' : '') });
    // §4: arm priority skip for the NEXT optional exercise
    armAutoSkipIfNextOptional('cumulative_fatigue');
  }

  // ----- §4 Priority auto-skip arm: also on neural_fatigue signal on this set -----
  if (setLogEntry.signal === 'neural_fatigue') {
    armAutoSkipIfNextOptional('neural_fatigue');
  }
}

function armAutoSkipIfNextOptional(reason) {
  const next = nextExerciseAfter(state.exIdx);
  if (!next) return;
  if (next.priority !== 'optional') return;
  if (state.pendingAutoSkipFor === next.id) return; // already armed
  state.pendingAutoSkipFor = next.id;
  state.lastAutoSkipTrigger = reason; // STORY-028/030 surface trigger to summary
  inspectorLog('rule', { tag: '§4 priority auto-skip armed', code: '§4 priority_skip_armed',
    msg: 'next exercise "' + next.name + '" is optional → will auto-skip (5s opt-out)',
    meta: 'trigger=' + reason });
}

function nextExerciseAfter(idx) {
  if (!state.workout) return null;
  return state.workout.exercises[idx + 1] || null;
}

/* -------------- 5. SCREEN: HOME -------------- */
/* v6 §6 — Home defaults to today's session. Week navigation is moved into a bottom-sheet. */
function pickDefaultWorkout() {
  // Sprint 2.5 (2026-06-10): cycle-based DB suggestion ha priorita' sul calendar match.
  // Se _dbSuggestedWorkoutId e' settato (calcolato da computeNextWorkoutIdFromDB dopo
  // load + completeSession), lo preferisce. Altrimenti fallback al calendar-based
  // legacy (che era il picker fragile pre-Sprint 2.5).
  if (_dbSuggestedWorkoutId) {
    const suggested = PROGRAM.workouts.find(w => w.id === _dbSuggestedWorkoutId);
    if (suggested) return suggested;
  }
  // Fallback legacy: current week + today's day; then current week's first; then first overall.
  const today = DAY_IT[['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][new Date().getDay()]];
  const wk = PROGRAM.current_week || 1;
  const inWeek = PROGRAM.workouts.filter(w => w.week === wk);
  const todayMatch = inWeek.find(w => w.day === today);
  if (todayMatch) return todayMatch;
  if (inWeek.length) return inWeek[0];
  return PROGRAM.workouts[0] || null;
}

function renderHome() {
  $('home-athlete').textContent = PROGRAM.athlete || '';
  const def = pickDefaultWorkout();
  if (!def) {
    $('home-today-eyebrow').textContent = 'Nessun allenamento';
    $('home-today-name').textContent = '—';
    $('home-today-meta').textContent = '';
    $('home-today-preview').innerHTML = '';
    $('start-btn').disabled = true;
    return;
  }
  state.selectedWeek = def.week;
  state.selectedWorkoutId = def.id;
  $('home-today-eyebrow').textContent = 'Oggi · Settimana ' + def.week;
  $('home-today-name').textContent = def.name;
  const totalSets = def.exercises.reduce((acc, e) => acc + e.sets.length, 0);
  $('home-today-meta').textContent = def.day + ' · ' + def.exercises.length + ' esercizi · ' + totalSets + ' set';
  // Quiet preview — exercises listed plainly, no pill chrome (§6 says preview is allowed as background detail).
  const preview = $('home-today-preview'); preview.innerHTML = '';
  def.exercises.forEach(ex => {
    const row = document.createElement('div');
    row.className = 'home-today-preview-row';
    row.innerHTML = '<span>' + ex.name + '</span><span>' + ex.sets.length + '×</span>';
    preview.appendChild(row);
  });
  $('start-btn').disabled = false;
  $('start-btn').textContent = 'Inizia';
}

/* "Vedi altri giorni" overlay — week + day picker for the rare case the athlete needs it. */
function openOtherDays() {
  // Render week pills
  const wkRow = $('week-row'); wkRow.innerHTML = '';
  for (let w = 1; w <= PROGRAM.duration_weeks; w++) {
    const hasData = PROGRAM.workouts.some(x => x.week === w);
    const pill = document.createElement('div');
    pill.className = 'week-pill'
      + (w === state.selectedWeek ? ' active' : '')
      + (hasData ? '' : ' empty');
    pill.textContent = 'Sett ' + w;
    pill.onclick = () => { state.selectedWeek = w; openOtherDays(); };
    wkRow.appendChild(pill);
  }
  // Day cards for that week
  const dc = $('day-cards'); dc.innerHTML = '';
  const wkWorkouts = PROGRAM.workouts.filter(w => w.week === state.selectedWeek);
  if (wkWorkouts.length === 0) {
    dc.innerHTML = '<div class="day-empty">Nessun allenamento questa settimana.</div>';
  } else {
    wkWorkouts.forEach(w => {
      const card = document.createElement('div');
      card.className = 'day-card' + (state.selectedWorkoutId === w.id ? ' active' : '');
      card.innerHTML = '<div class="day-card-name">' + w.name + '</div>'
                     + '<div class="day-card-day">' + w.day + '</div>';
      card.onclick = () => {
        state.selectedWorkoutId = w.id;
        const chosen = PROGRAM.workouts.find(x => x.id === w.id);
        if (chosen) {
          $('home-today-eyebrow').textContent = 'Settimana ' + chosen.week + ' · ' + chosen.day;
          $('home-today-name').textContent = chosen.name;
          const ts = chosen.exercises.reduce((a,e)=>a+e.sets.length,0);
          $('home-today-meta').textContent = chosen.exercises.length + ' esercizi · ' + ts + ' set';
          const pv = $('home-today-preview'); pv.innerHTML = '';
          chosen.exercises.forEach(ex => {
            const row = document.createElement('div');
            row.className = 'home-today-preview-row';
            row.innerHTML = '<span>' + ex.name + '</span><span>' + ex.sets.length + '×</span>';
            pv.appendChild(row);
          });
          $('start-btn').disabled = false;
        }
        closeOtherDays();
      };
      dc.appendChild(card);
    });
  }
  $('other-days-overlay').classList.add('visible');
}
function closeOtherDays(e) {
  if (e && e.target.closest && e.target.closest('.ovr-sheet')) return;
  $('other-days-overlay').classList.remove('visible');
}

function openExPreview(ex) {
  $('ex-preview-title').textContent = ex.name;
  const badge = $('ex-preview-badge');
  badge.className = 'badge ' + categoryBadgeClass(ex.category);
  badge.textContent = ex.category;
  const meta = [];
  if (ex.priority && ex.priority !== 'main') meta.push('priorità: ' + ex.priority);
  if (ex.neuro_type && ex.neuro_type !== 'core') meta.push('neuro: ' + ex.neuro_type);
  if (ex.goal) meta.push('goal override: ' + ex.goal);
  $('ex-preview-meta').textContent = meta.join(' · ');
  const setsWrap = $('ex-preview-sets'); setsWrap.innerHTML = '';
  ex.sets.forEach(s => {
    const div = document.createElement('div');
    div.className = 'preview-set';
    let target = '';
    if (s.kind === 'reps') target = s.reps + ' reps · ' + fmtLoad(s.load);
    else if (s.kind === 'duration') target = s.duration + ' s';
    else if (s.kind === 'open') target = (s.range || 'open') + (s.load !== null ? ' · ' + fmtLoad(s.load) : '');
    div.innerHTML = '<div class="preview-set-target">Set ' + s.order + ' · ' + target + '</div>'
      + (s.notes ? '<div class="preview-set-note">' + s.notes + '</div>' : '');
    setsWrap.appendChild(div);
  });
  $('ex-preview-overlay').classList.add('visible');
}
function closeExPreview(e) { if (e && e.target.id !== 'ex-preview-overlay' && e.type === 'click' && e.target.closest('.ovr-sheet')) return; $('ex-preview-overlay').classList.remove('visible'); }

/* -------------- 6. WORKOUT START -------------- */
function startWorkout() {
  const w = PROGRAM.workouts.find(w => w.id === state.selectedWorkoutId);
  if (!w) return;
  state.workout = w;
  state.exIdx = 0;
  state.setIdx = 0;
  state.preloadedNullLoad = null;
  state.loadOverrides = {};
  state.setLog = [];
  state.perExerciseLogs = {};
  state.cumulativeFiredFor = new Set();
  state.severeFiredFor = new Set();
  state.startedExerciseSequence = [];
  state.pendingDispatcherMessage = null;
  state.pendingRestExtraSeconds = 0;
  state.pendingAutoSkipFor = null;
  state.perSetCapture = {};
  // F0004 — reset PS-1 volume counters and PS-3 last-decision tracker per session
  state.weeklyVolume = {
    thisSession: { push: 0, pull: 0, squat: 0, hinge: 0, core: 0, skill: 0 },
    lastBand:    { push: 'below_mev', pull: 'below_mev', squat: 'below_mev',
                   hinge: 'below_mev', core: 'below_mev', skill: 'n/a' },
    firedCaps:   { push: {}, pull: {}, squat: {}, hinge: {}, core: {}, skill: {} },
  };
  state.lastInterExRestDecision = null;
  // F0004 PS-4c — reset joint readiness on every session start
  state.jointReadiness = { wrist: null, elbow: null, shoulder: null, lower_back: null };
  state.inspector.log = [];
  state.inspector.rulesFired = {};
  inspectorLog('set', { tag: 'workout start', msg: w.name + ' · ' + w.day, meta: 'goal=' + (PROGRAM.goal||'hypertrophy') });
  // Sprint 2.2 vero — crea la riga sessions in Postgres (fire-and-forget).
  // Se DB down o RLS blocca, l'UI continua a funzionare in-memory come v7.x.
  insertSession().catch(e => console.error('[db] insertSession error:', e));
  // v7.1 — route through PS-4c check-in screen before the first exercise
  showCheckin();
}

/* ───── v7.1 PS-4c — Joint readiness check-in ───── */
const JOINT_LABELS = {
  wrist:      'Polso',
  elbow:      'Gomito',
  shoulder:   'Spalla',
  lower_back: 'Lombare',
};

function showCheckin() {
  // Default state: simple Sì / qualcosa fa male
  $('checkin-default').style.display = 'block';
  $('checkin-detailed').style.display = 'none';
  // Reset joint pills
  resetCheckin();
  show('checkin');
}
function confirmCheckinOk() {
  // No joint flags set; proceed straight into the workout
  inspectorLog('rule', { tag: 'PS-4c check-in', code: 'PS-4c readiness',
    msg: 'Tutto ok confermato', meta: 'no joint flags · default Sì' });
  enterExercise(0);
}
function tapSomethingHurts() {
  $('checkin-default').style.display = 'none';
  $('checkin-detailed').style.display = 'block';
  // Render the joint grid lazily
  const grid = $('joint-grid'); grid.innerHTML = '';
  Object.keys(JOINT_LABELS).forEach(joint => {
    const row = document.createElement('div');
    row.className = 'joint-row';
    row.innerHTML =
        '<div class="joint-row-name">' + JOINT_LABELS[joint] + '</div>'
      + '<button class="joint-pill" data-joint="' + joint + '" data-status="fastidio" onclick="pickJointStatus(\'' + joint + '\', \'fastidio\')">Fastidio</button>'
      + '<button class="joint-pill" data-joint="' + joint + '" data-status="dolore" onclick="pickJointStatus(\'' + joint + '\', \'dolore\')">Dolore</button>';
    grid.appendChild(row);
  });
}
function pickJointStatus(joint, status) {
  // Toggle: tapping the same pill clears it
  if (state.jointReadiness[joint] === status) {
    state.jointReadiness[joint] = null;
  } else {
    state.jointReadiness[joint] = status;
  }
  // Re-render pill states
  document.querySelectorAll('.joint-pill').forEach(p => {
    const j = p.getAttribute('data-joint');
    const s = p.getAttribute('data-status');
    if (state.jointReadiness[j] === s) p.classList.add('selected');
    else p.classList.remove('selected');
  });
}
function resetCheckin() {
  state.jointReadiness = { wrist: null, elbow: null, shoulder: null, lower_back: null };
  document.querySelectorAll('.joint-pill').forEach(p => p.classList.remove('selected'));
  $('checkin-default').style.display = 'block';
  $('checkin-detailed').style.display = 'none';
}
function confirmCheckinDetailed() {
  const flagged = Object.entries(state.jointReadiness).filter(([j,s]) => s !== null);
  if (flagged.length === 0) {
    // No flags actually set — same as default Sì path
    return confirmCheckinOk();
  }
  inspectorLog('rule', { tag: 'PS-4c check-in', code: 'PS-4c readiness',
    msg: 'Joint flags: ' + flagged.map(([j,s]) => j+'='+s).join(', '),
    meta: 'fastidio → load -5%; dolore → silent skip + post-session override' });
  state.inspector.events.push({
    kind: 'joint_readiness', t: Date.now(),
    meta: { flags: Object.assign({}, state.jointReadiness) }
  });
  enterExercise(0);
}

/* Returns the worst-case status across an exercise's joint_load list, or null. */
function exerciseJointStatus(ex) {
  if (!ex || !ex.joint_load || ex.joint_load.length === 0) return null;
  let worst = null;
  for (const j of ex.joint_load) {
    const s = state.jointReadiness[j];
    if (s === 'dolore') return 'dolore';
    if (s === 'fastidio') worst = 'fastidio';
  }
  return worst;
}

function enterExercise(idx) {
  if (!state.workout) return;
  const ex = state.workout.exercises[idx];
  if (!ex) { return finishWorkout(); }
  state.exIdx = idx; state.setIdx = 0; state.preloadedNullLoad = null;

  // §4 Priority auto-skip check — fires before exercise begins
  if (state.pendingAutoSkipFor === ex.id && ex.priority === 'optional') {
    state.pendingAutoSkipFor = null;
    return showAutoSkip(ex);
  }

  // v7.1 PS-4c — joint readiness gate, BEFORE the exercise starts.
  //   dolore on any joint in joint_load → silent skip + post-session override (gap §9)
  //   fastidio on any joint in joint_load → -5% load on all sets + coach pill
  const jointStatus = exerciseJointStatus(ex);
  if (jointStatus === 'dolore') {
    inspectorLog('rule', { tag: 'PS-4c silent skip (dolore)', code: 'PS-4c joint_skip',
      msg: ex.name + ' silent-skipped — dolore on ' + (ex.joint_load||[]).join(','),
      meta: 'safety route, surfaces in post-session override (Complete screen)' });
    state.inspector.events.push({
      kind: 'auto_skip', exId: ex.id, exName: ex.name,
      code: 'PS-4c joint_skip', t: Date.now(),
      meta: { trigger: 'joint_dolore', overridden: false, silent: true,
              joints: ex.joint_load.filter(j => state.jointReadiness[j] === 'dolore') }
    });
    state.exIdx += 1;
    if (state.exIdx >= state.workout.exercises.length) return finishWorkout();
    return enterExercise(state.exIdx);
  }
  if (jointStatus === 'fastidio') {
    const flaggedJoints = ex.joint_load.filter(j => state.jointReadiness[j] === 'fastidio');
    let anyLoadReduced = false;
    ex.sets.forEach(s => {
      if (s.kind === 'reps' && s.load !== null && s.load !== 0) {
        const overrideKey = ex.id + '#' + s.order;
        const cur = state.loadOverrides[overrideKey] ?? s.load;
        state.loadOverrides[overrideKey] = roundHalf(cur * 0.95);
        anyLoadReduced = true;
      }
    });
    const jointTxt = flaggedJoints.map(j => JOINT_LABELS[j].toLowerCase()).join(' / ');
    if (anyLoadReduced) {
      inspectorLog('rule', { tag: 'PS-4c downgrade (fastidio)', code: 'PS-4c joint_downgrade',
        msg: ex.name + ' — load -5% on all sets (fastidio: ' + flaggedJoints.join(',') + ')',
        meta: 'PS-4c soft brake; bounded inside coach load ±10%' });
      dispatch('Carico ridotto del 5% per proteggere il ' + jointTxt + '.',
               'warn', { code: 'PS-4c joint_downgrade' });
    } else {
      // null-load / bodyweight / duration: no numeric load to reduce. Still warn the athlete.
      inspectorLog('rule', { tag: 'PS-4c attention (fastidio, no load lever)', code: 'PS-4c joint_attention',
        msg: ex.name + ' — fastidio on ' + flaggedJoints.join(',') + ' but no load to reduce',
        meta: 'PS-4c soft brake n/a (bodyweight or null load); coach message only' });
      dispatch('Attento al ' + jointTxt + ' in questo esercizio.',
               'warn', { code: 'PS-4c joint_attention' });
    }
  }

  // Track started exercise sequence (used by §5 neural stack)
  state.startedExerciseSequence.push(ex.id);
  // §5 Neural stack — log when 3+ consecutive neural starts
  const stackCount = neuralStackCountAtStartOf(ex.id);
  if (stackCount >= 3) {
    inspectorLog('rule', { tag: '§5 neural stack', code: '§5 neural_stack',
      msg: stackCount + ' consecutive neural exercises — tolerance reduced + rest bumped',
      meta: 'hard+dirty will produce reduce action; +rest_delta on inter-exercise rest' });
  }

  // null-load all-sets exercise → ask preload
  if (ex.sets.every(s => (s.kind === 'reps' || s.kind === 'open') && s.load === null) && ex.sets.length > 0) {
    return showPreload(ex);
  }
  showExecute();
}

function showPreload(ex) {
  $('preload-ex-name').textContent = ex.name;
  $('preload-input').value = '';
  $('preload-error').textContent = '';
  show('preload');
  setTimeout(()=>$('preload-input').focus(), 200);
}
function confirmPreload() {
  const v = parseFloat($('preload-input').value);
  if (isNaN(v) || v < 0) { $('preload-error').textContent = 'Inserisci un valore valido'; return; }
  state.preloadedNullLoad = v;
  showExecute();
}

/* -------------- 7. SCREEN: EXECUTE -------------- */
function currentEx() { return state.workout.exercises[state.exIdx]; }
function currentSet() { return currentEx().sets[state.setIdx]; }

function effectiveLoadForCurrentSet() {
  const ex = currentEx(); const s = currentSet();
  const overrideKey = ex.id + '#' + s.order;
  if (state.loadOverrides[overrideKey] !== undefined) return state.loadOverrides[overrideKey];
  if (s.load === null && state.preloadedNullLoad !== null) return state.preloadedNullLoad;
  return s.load;
}

function showExecute() {
  const ex = currentEx(); const s = currentSet();
  const totalSets = ex.sets.length;

  $('exec-meta').textContent = state.workout.name + ' · Set ' + s.order + '/' + totalSets;
  const badge = $('exec-badge');
  badge.className = 'badge ' + categoryBadgeClass(ex.category);
  badge.textContent = ex.category;

  $('exec-prog').style.width = (100 * (s.order - 1) / totalSets) + '%';
  $('exec-ex-name').textContent = ex.name;

  // v6 §8 — single hero with target reps/duration. No all-caps GO label, no eyebrow.
  const hero = $('exec-target-hero');
  if (s.kind === 'duration') {
    hero.innerHTML = s.duration + '<span class="unit">secondi · tieni la posizione</span>';
  } else if (s.kind === 'reps') {
    hero.innerHTML = s.reps + '<span class="unit">reps</span>';
  } else if (s.kind === 'open') {
    hero.innerHTML = (s.range || 'open') + '<span class="unit">reps</span>';
  } else {
    hero.innerHTML = '—';
  }

  // v6 §5 — single load model. Prescription is the silent default. The line itself is the edit affordance.
  const load = effectiveLoadForCurrentSet();
  const loadLine = $('exec-load-line');
  if (s.kind === 'duration') {
    loadLine.style.display = 'none';
  } else {
    loadLine.style.display = 'block';
    if (load === null) {
      loadLine.innerHTML = 'Carico da impostare<span class="edit-hint">tocca per inserire</span>';
    } else {
      loadLine.innerHTML = fmtLoad(load) + '<span class="edit-hint">tocca per cambiare</span>';
    }
    const overrideKey = ex.id + '#' + s.order;
    if (state.loadOverrides[overrideKey] !== undefined) loadLine.classList.add('adapted');
    else loadLine.classList.remove('adapted');
  }

  if (s.notes) {
    $('exec-card-note').textContent = s.notes;
    $('exec-card-note').style.display = 'block';
  } else {
    $('exec-card-note').style.display = 'none';
  }

  // §8 Dispatcher — render coach message ONLY when one is pending. v6 §11: no "In linea." default daemon-status copy.
  const sysWrap = $('exec-sys-wrap');
  sysWrap.innerHTML = '';
  if (state.pendingDispatcherMessage) {
    const m = state.pendingDispatcherMessage;
    const pill = document.createElement('div');
    pill.className = 'exec-sys ' + (m.severity === 'warn' ? 'warn' : (m.severity === 'info' ? 'info' : ''));
    pill.textContent = m.msg;
    sysWrap.appendChild(pill);
    state.pendingDispatcherMessage = null;
  }

  // Reset the hold-to-fail button state on every set
  const fb = $('exec-fail-btn'); if (fb) { fb.classList.remove('holding','fired'); }

  // hide overlays
  $('null-overlay').classList.remove('visible');
  if ($('load-edit-overlay')) $('load-edit-overlay').classList.remove('visible');
  show('execute');
}

/* -------- DONE flow (v6 §5: prescription is the silent default) -------- */
function tapDone() {
  const ex = currentEx(); const s = currentSet();
  const load = effectiveLoadForCurrentSet();

  // Null-load safety net only — only fires when prescription is genuinely null AND not yet resolved.
  // (v6 removes the prescription-confirmation overlay entirely.)
  if ((s.kind === 'reps' || s.kind === 'open') && (load === null)) {
    $('null-overlay-sub').textContent = 'Per questa serie';
    $('null-load-input').value = '';
    $('null-load-error').textContent = '';
    $('null-overlay').classList.add('visible');
    setTimeout(()=>$('null-load-input').focus(),200);
    return;
  }
  // v7 fix — kind='open' (target_reps_range like "technical failure") needs an explicit
  // rep capture; otherwise the set logs with actual=null and the coach loses the count.
  if (s.kind === 'open') {
    $('open-overlay-sub').textContent = 'Range prescritto: ' + (s.range || 'tecnica al cedimento');
    $('open-reps-input').value = '';
    $('open-reps-error').textContent = '';
    $('open-overlay').classList.add('visible');
    setTimeout(()=>$('open-reps-input').focus(),200);
    return;
  }
  logSetAsPrescribed();
}
function dismissOpenOverlay(e) {
  if (e && e.target.closest && e.target.closest('.ovr-sheet')) return;
  $('open-overlay').classList.remove('visible');
}
function confirmOpenReps() {
  const v = parseInt($('open-reps-input').value, 10);
  if (isNaN(v) || v < 0) { $('open-reps-error').textContent = 'Inserisci un valore valido'; return; }
  $('open-overlay').classList.remove('visible');
  logSetAsPrescribed(/*overrideLoad*/ undefined, /*explicitActual*/ v);
}
function dismissNullOverlay(e) {
  if (e && e.target.closest('.ovr-sheet')) return;
  $('null-overlay').classList.remove('visible');
}
function confirmNullLoad() {
  const v = parseFloat($('null-load-input').value);
  if (isNaN(v) || v < 0) { $('null-load-error').textContent = 'Inserisci un valore valido'; return; }
  const ex = currentEx(); const s = currentSet();
  state.loadOverrides[ex.id + '#' + s.order] = v;
  $('null-overlay').classList.remove('visible');
  logSetAsPrescribed();
}

/* v6 §5 — Inline load edit. The Execute "load line" itself is the edit affordance; no overlay interrupts. */
function editLoadInline() {
  const ex = currentEx(); const s = currentSet();
  if (!ex || !s || s.kind === 'duration') return;
  const load = effectiveLoadForCurrentSet();
  $('load-edit-sub').textContent = (load === null)
    ? 'Inserisci il carico per questa serie.'
    : 'Prescritto: ' + fmtLoad(load) + '. Cambia se hai usato un altro carico.';
  $('load-edit-input').value = (load === null || load === 0) ? '' : load;
  $('load-edit-error').textContent = '';
  $('load-edit-overlay').classList.add('visible');
  setTimeout(()=>$('load-edit-input').focus(),180);
}
function dismissLoadEdit(e) {
  if (e && e.target.closest && e.target.closest('.ovr-sheet')) return;
  $('load-edit-overlay').classList.remove('visible');
}
function saveLoadEdit() {
  const v = parseFloat($('load-edit-input').value);
  if (isNaN(v) || v < 0) { $('load-edit-error').textContent = 'Inserisci un valore valido'; return; }
  const ex = currentEx(); const s = currentSet();
  state.loadOverrides[ex.id + '#' + s.order] = v;
  $('load-edit-overlay').classList.remove('visible');
  showExecute(); // re-render so the load line reflects the new value
}

/* v6 §1 — Hold-to-confirm "Non ce l'ho fatta". Friction proportional to consequence. */
let _failHoldTimer = null;
function failHoldStart() {
  const btn = $('exec-fail-btn');
  if (!btn) return;
  btn.classList.add('holding');
  _failHoldTimer = setTimeout(() => {
    btn.classList.add('fired');
    btn.classList.remove('holding');
    _failHoldTimer = null;
    tapFatigue(); // jump into the single fail screen (reps + auto-rest)
  }, 1500);
}
function failHoldCancel() {
  const btn = $('exec-fail-btn');
  if (btn) btn.classList.remove('holding');
  if (_failHoldTimer) { clearTimeout(_failHoldTimer); _failHoldTimer = null; }
}
function logSetAsPrescribed(overrideLoad, explicitActual) {
  const ex = currentEx(); const s = currentSet();
  const load = overrideLoad !== undefined ? overrideLoad : effectiveLoadForCurrentSet();
  const target = s.kind === 'reps' ? s.reps : (s.kind === 'duration' ? s.duration : null);
  // v7 — for kind='open' the caller passes explicitActual; otherwise actual=target.
  const actual = (explicitActual !== undefined) ? explicitActual : target;
  const entry = {
    exId: ex.id, order: s.order, kind: s.kind, target, actual, load,
    effort: null, tech: null, signal: null, fired: [],
  };
  state.setLog.push(entry);
  state.pendingCaptureFor = entry;
  // Sprint 2.3 — persist set_logs (fire-and-forget)
  insertSetLog(entry, ex, s).catch(e => console.error('[db] insertSetLog error:', e));
  evaluateRulesAfterSet(entry);
  proceedAfterSet();
}

/* -------- FAIL flow (v6 §4 + §7: single screen, coach voice — no "Cedimento" jargon) -------- */
function tapFatigue() {
  const ex = currentEx(); const s = currentSet();
  $('fatigue-context').textContent = ex.name + ' · Set ' + s.order;
  if (s.kind === 'duration') {
    $('fatigue-question').textContent = 'Quanti secondi hai tenuto?';
    $('fatigue-unit').textContent = 'sec';
  } else {
    $('fatigue-question').textContent = 'Quante ne hai fatte?';
    $('fatigue-unit').textContent = 'reps';
  }
  $('fatigue-num').value = '';
  $('fatigue-num-error').textContent = '';
  $('fatigue-load-error').textContent = '';
  // null-load safety net inside fail path (only when prescription is genuinely null)
  const load = effectiveLoadForCurrentSet();
  if ((s.kind === 'reps' || s.kind === 'open') && load === null) {
    $('fatigue-load-group').style.display = 'block';
    $('fatigue-load-input').value = '';
  } else {
    $('fatigue-load-group').style.display = 'none';
  }
  show('fatigue');
  setTimeout(()=>$('fatigue-num').focus(),200);
}
function cancelFatigue() { showExecute(); }
function confirmFatigue() {
  const ex = currentEx(); const s = currentSet();
  const v = parseFloat($('fatigue-num').value);
  if (isNaN(v) || v < 0) { $('fatigue-num-error').textContent = 'Inserisci un valore valido'; return; }
  let load = effectiveLoadForCurrentSet();
  if ((s.kind === 'reps' || s.kind === 'open') && load === null) {
    const lv = parseFloat($('fatigue-load-input').value);
    if (isNaN(lv) || lv < 0) { $('fatigue-load-error').textContent = 'Inserisci il carico'; return; }
    state.loadOverrides[ex.id + '#' + s.order] = lv;
    load = lv;
  }
  const target = s.kind === 'reps' ? s.reps : (s.kind === 'duration' ? s.duration : null);
  const entry = {
    exId: ex.id, order: s.order, kind: s.kind, target, actual: v, load,
    effort: null, tech: null, signal: null, fired: [],
    fatigue_path: true,
  };
  state.setLog.push(entry);
  state.pendingCaptureFor = entry;
  // Sprint 2.3 — persist set_logs (fire-and-forget)
  insertSetLog(entry, ex, s).catch(e => console.error('[db] insertSetLog error:', e));
  evaluateRulesAfterSet(entry);
  proceedAfterSet();
}

/* v6 §2 + §7 — After a set is logged, always go straight to rest. Capture screen is deleted. */
function proceedAfterSet() {
  state.pendingCaptureFor = null; // capture path retired in v6
  goToRestOrNext();
}

/* v6 §6: manual skip is no longer surfaced on Execute. These functions are kept as
   no-ops so any stale references don't throw, and callable from a future overflow menu. */
function tapSkipExercise() { /* v6: removed from Execute — see complete-skips for post-session override */ }
function dismissSkipOverlay() {}
function confirmSkipExercise() {}

/* -------- ROUTING after a set -------- */
function goToRestOrNext() {
  const ex = currentEx(); const s = currentSet();
  // last set of this exercise?
  if (state.setIdx + 1 < ex.sets.length) {
    // rest between sets within exercise
    const baseRest = s.rest ?? 0;
    const extra = state.pendingRestExtraSeconds || 0;
    state.pendingRestExtraSeconds = 0;
    startRest(baseRest + extra, () => {
      state.setIdx += 1;
      showExecute();
    });
  } else {
    // moving to next exercise → inter-exercise rest with §6 / F0004 PS-3 logic
    const nextEx = state.workout.exercises[state.exIdx + 1];
    if (!nextEx) return finishWorkout();
    const goal = resolveGoal(ex, state.workout, PROGRAM);
    const programmed = nextEx.inter_rest ?? 0;
    const prevSt = ex.stimulus_type;
    const nextSt = nextEx.stimulus_type;
    let interRest = resolveInterExRest(programmed, prevSt, nextSt, goal);
    state.lastInterExRestDecision = {
      from: ex.name, fromStimulus: prevSt,
      to:   nextEx.name, toStimulus: nextSt,
      programmed, resolved: interRest, goal,
    };
    if (interRest !== programmed) {
      inspectorLog('rule', { tag: '§6 / PS-3 inter-exercise rest', code: '§6 inter_ex_rest',
        msg: prevSt + ' → ' + nextSt + ': programmed ' + programmed + 's → ' + interRest + 's',
        meta: 'PS-3 stimulus-typed floor; coach honored when ≥120s (DL-11)' });
    }
    const extra = state.pendingRestExtraSeconds || 0;
    state.pendingRestExtraSeconds = 0;
    if (interRest + extra <= 0) {
      // no rest → transition directly
      transitionTo(state.exIdx + 1);
    } else {
      startRest(interRest + extra, () => {
        transitionTo(state.exIdx + 1);
      }, /*nextIsExercise*/ true);
    }
  }
}

/* -------- 8. SCREEN: REST -------- */
function startRest(seconds, onComplete, nextIsExercise) {
  state.rest.totalSeconds = seconds;
  state.rest.secondsLeft = seconds;
  state.rest.onComplete = onComplete;
  // Update next preview
  const ex = currentEx();
  const isLastSet = (state.setIdx + 1) >= ex.sets.length;
  if (nextIsExercise || isLastSet) {
    const nextEx = state.workout.exercises[state.exIdx + 1];
    if (nextEx) {
      $('rest-next').textContent = nextEx.name;
      const firstSet = nextEx.sets[0];
      const firstLoad = state.loadOverrides[nextEx.id+'#'+firstSet.order] ?? firstSet.load;
      $('rest-next-load').textContent = firstSet.kind === 'duration'
        ? firstSet.duration + ' s'
        : (firstSet.kind === 'reps' ? firstSet.reps + ' reps · ' + fmtLoad(firstLoad)
                                    : (firstSet.range||'open'));
    } else {
      $('rest-next').textContent = 'Fine allenamento';
      $('rest-next-load').textContent = '';
    }
  } else {
    const nextSet = ex.sets[state.setIdx + 1];
    $('rest-next').textContent = ex.name + ' · Set ' + nextSet.order;
    const nLoad = state.loadOverrides[ex.id+'#'+nextSet.order] ?? nextSet.load;
    $('rest-next-load').textContent = nextSet.kind === 'duration'
      ? nextSet.duration + ' s'
      : (nextSet.kind === 'reps' ? nextSet.reps + ' reps · ' + fmtLoad(nLoad)
                                 : (nextSet.range||'open'));
  }

  // Capture is collected on screen-capture before rest — no chip reset needed here.

  $('rest-display').textContent = fmtMMSS(seconds);
  $('rest-arc').setAttribute('stroke-dashoffset', 0);
  $('rest-skip-idle').style.display = 'block';
  $('rest-skip-confirm').style.display = 'none';
  show('rest');

  // animate
  if (state.rest.timerId) clearInterval(state.rest.timerId);
  state.rest.timerId = setInterval(() => {
    state.rest.secondsLeft -= 1;
    if (state.rest.secondsLeft <= 0) {
      finishRest();
      return;
    }
    $('rest-display').textContent = fmtMMSS(state.rest.secondsLeft);
    const frac = 1 - state.rest.secondsLeft / state.rest.totalSeconds;
    $('rest-arc').setAttribute('stroke-dashoffset', String(C * frac));
  }, 1000);
}
function finishRest() {
  if (state.rest.timerId) clearInterval(state.rest.timerId);
  state.rest.timerId = null;
  // Capture (if any) is now collected on the dedicated screen-capture before rest,
  // so by the time we reach finishRest there is nothing left to apply.
  const cb = state.rest.onComplete;
  state.rest.onComplete = null;
  if (cb) cb();
}
function promptSkipRest() {
  $('rest-skip-prompt').textContent = 'Saltare ' + fmtMMSS(state.rest.secondsLeft) + ' di riposo?';
  $('rest-skip-idle').style.display = 'none';
  $('rest-skip-confirm').style.display = 'flex';
}
function cancelSkipRest() {
  $('rest-skip-confirm').style.display = 'none';
  $('rest-skip-idle').style.display = 'block';
}
function skipRest() {
  state.rest.secondsLeft = 0;
  finishRest();
}

/* -------- 8b. CAPTURE — v6 §2: deleted entirely. Field data: 0 of 19 sets had values filled.
   Stubs left as no-ops in case any legacy reference fires; they touch no removed DOM. -------- */
function showCaptureScreen() { /* removed in v6 — proceedAfterSet always goes to rest */ }
function pickEffort() {}
function pickTech() {}
function maybeAutoAdvanceCapture() {}
function skipCapture() { state.pendingCaptureFor = null; goToRestOrNext(); }
function resetCaptureChips() {}
function applyCapture() { state.pendingCaptureFor = null; }
function evaluateMainRulesOnly(setLogEntry) {
  const ex = findExercise(setLogEntry.exId);
  if (!ex) return;
  const inScope = ['main_lift','accessory'].includes(ex.category);
  if (!inScope) return;
  const goal = resolveGoal(ex, state.workout, PROGRAM);
  const stackActive = neuralStackActive(state.startedExerciseSequence);
  const map = mapSignal(setLogEntry.effort, setLogEntry.tech);
  setLogEntry.signal = map.signal;
  if (map.fallback) {
    inspectorLog('rule', { tag: 'DL-9 fallback', code: 'DL-9 fallback',
      msg: 'unmapped pair (' + setLogEntry.effort + ',' + setLogEntry.tech + ') → managed_fatigue',
      meta: 'telemetry flag emitted' });
  }
  const action = mainRule(map.signal, goal, stackActive);
  if (action) {
    const isSilentFamily = SILENT_RULE_CODES.has(action.code); // STORY-024
    const skipLoadDelta = isSilentFamily &&
      (action.code === '§1 underload' || action.code === '§1 overload'); // STORY-025
    const remaining = ex.sets.filter(s => s.order > setLogEntry.order);
    if (action.load_delta !== 0 && !skipLoadDelta) {
      remaining.forEach(s => {
        if (s.kind === 'reps' && s.load !== null && s.load !== 0) {
          const cur = state.loadOverrides[ex.id+'#'+s.order] ?? s.load;
          const newLoad = applyLoadDelta(cur, action.load_delta);
          state.loadOverrides[ex.id+'#'+s.order] = newLoad;
        }
      });
    }
    if (action.rest_delta > 0 && !isSilentFamily) state.pendingRestExtraSeconds += action.rest_delta;
    dispatch(action.msg, action.action === 'reduce' ? 'warn' : 'info',
             { silent: isSilentFamily, code: action.code });
    /* STORY-030 ledger entry (capture path) — see twin in evaluateRulesAfterSet. */
    state.inspector.events.push({
      kind: isSilentFamily ? 'rule_silent' : 'rule', exId: ex.id, exName: ex.name,
      code: action.code, t: Date.now(),
      meta: { silent: isSilentFamily, action: action.action, signal: map.signal, capture: true }
    });
    inspectorLog('rule', { tag: action.code, code: action.code,
      msg: action.msg, meta: 'goal=' + goal +
      ' load_Δ=' + (skipLoadDelta ? 0 : action.load_delta) +
      ' rest_Δ=+' + (isSilentFamily ? 0 : action.rest_delta) + 's (capture)' +
      (isSilentFamily ? ' (silent log)' : '') +
      (skipLoadDelta ? ' (load_Δ removed by STORY-025)' : '') });
  }
  // §3 cumulative fatigue check using captured signal stream
  const perEx = state.perExerciseLogs[ex.id] = state.perExerciseLogs[ex.id] || [];
  // ensure captured set is in perEx (it was pushed in evaluateRulesAfterSet)
  const cum = checkCumulativeFatigue(ex.id, perEx);
  if (cum) {
    state.cumulativeFiredFor.add(ex.id);
    /* STORY-029: §3 input-degraded mode — when the 3 sets in the window have null effort/tech,
       reword the message to remove the "(effort)"/"(technique)" attribution and emit input_degraded=true. */
    const last3 = perEx.slice(-3);
    const inputDegraded = last3.every(s => !s.effort && !s.tech);
    const msg = inputDegraded
      ? 'Tre set in calo. Fermo qui i pesanti — passa a volume controllato.'
      : 'Tre set in calo. Fermo qui i pesanti — passa a volume controllato.';
    dispatch(msg, 'warn', { code: '§3 cumulative_fatigue' });
    state.inspector.events.push({
      kind: 'cumulative_fatigue', exId: ex.id, exName: ex.name,
      code: '§3 cumulative_fatigue', t: Date.now(),
      meta: { input_degraded: inputDegraded, match: cum.match, capture: true }
    });
    inspectorLog('rule', { tag: '§3 cumulative fatigue', code: '§3 cumulative_fatigue',
      msg: inputDegraded
        ? 'progression matched on last 3 sets (input_degraded=true)'
        : 'progression matched (' + cum.match + ') over last 3 sets',
      meta: 'fired once per exercise per session (idempotent)' +
            (inputDegraded ? ' input_degraded=true' : '') });
    armAutoSkipIfNextOptional('cumulative_fatigue');
  }
  if (setLogEntry.signal === 'neural_fatigue') armAutoSkipIfNextOptional('neural_fatigue');
}

/* -------- 9. TRANSITION -------- */
function transitionTo(nextIdx) {
  const next = state.workout.exercises[nextIdx];
  if (!next) return finishWorkout();
  // §4 priority auto-skip is checked inside enterExercise()
  $('trans-name').textContent = next.name;
  $('trans-num').textContent = '3';
  show('transition');
  let n = 3;
  if (state.trans.timerId) clearInterval(state.trans.timerId);
  state.trans.timerId = setInterval(() => {
    n -= 1;
    if (n <= 0) {
      clearInterval(state.trans.timerId);
      state.trans.timerId = null;
      enterExercise(nextIdx);
    } else {
      $('trans-num').textContent = String(n);
    }
  }, 800);
}

/* -------- 10. AUTO-SKIP (§4) — v6 §9: SILENT skip, no overlay, no in-flow override --------
   Field evidence: athlete didn't notice and that was the intended UX. The override moves
   to post-session ("you skipped X — do it now or schedule?") on the Complete screen. */
function showAutoSkip(ex) {
  state.autoSkip.targetEx = ex;
  state.autoSkip.trigger = state.lastAutoSkipTrigger || 'fatigue';
  inspectorLog('rule', { tag: '§4 priority auto-skip (silent)', code: '§4 priority_skip',
    msg: ex.name + ' silent-skipped', meta: 'priority=optional + fatigue trigger=' + state.autoSkip.trigger });
  state.inspector.events.push({
    kind: 'auto_skip', exId: ex.id, exName: ex.name,
    code: '§4 priority_skip', t: Date.now(),
    meta: { trigger: state.autoSkip.trigger || 'fatigue', overridden: false, silent: true }
  });
  // Advance past the silent-skipped exercise without showing any UI for it.
  state.exIdx += 1;
  if (state.exIdx >= state.workout.exercises.length) return finishWorkout();
  enterExercise(state.exIdx);
}
function autoSkipOverride() { /* v6: no-op — override surfaces post-session on Complete (gap §9) */ }

/* -------- 11. WORKOUT COMPLETE (v6 §10: athlete-only audience) -------- */
function finishWorkout() {
  const log = state.setLog;
  const w = state.workout;
  const inScopeIds = new Set(w.exercises.filter(e => ['main_lift','accessory'].includes(e.category)).map(e=>e.id));
  let prescribed = 0, completed = 0, repsRatio = 0, repsCount = 0;
  w.exercises.forEach(e => { if (inScopeIds.has(e.id)) prescribed += e.sets.length; });
  log.forEach(entry => {
    const ex = w.exercises.find(e=>e.id===entry.exId);
    if (!ex || !inScopeIds.has(ex.id)) return;
    if (entry.kind === 'reps' && entry.target > 0) {
      repsRatio += Math.min(1, entry.actual / entry.target);
      repsCount += 1;
    } else {
      repsRatio += 1; repsCount += 1;
    }
    completed += 1;
  });
  const rate = prescribed > 0 ? (repsCount > 0 ? repsRatio / prescribed : 0) : 1;
  const exerciseCount = new Set(log.map(l=>l.exId)).size;

  $('cstat-sets').textContent = completed + '/' + prescribed;
  $('cstat-rate').textContent = Math.round(rate * 100) + '%';
  $('cstat-exs').textContent = exerciseCount;

  // v6 §11 — coach voice insight (declarative, specific). One line.
  let insight;
  if (state.cumulativeFiredFor.size > 0) insight = 'Hai gestito un calo di performance. Il sistema ha protetto il volume.';
  else if (state.severeFiredFor.size > 0) insight = 'Carico ridotto in corsa. Hai mantenuto qualità e struttura.';
  else if (rate >= 0.95) insight = 'Esecuzione precisa. Il programma avanza.';
  else insight = 'Sessione registrata. Buona qualità complessiva.';
  $('complete-insight').textContent = insight;

  // v6 §9 — Post-session override: list silent-skipped + manually-skipped exercises with two actions:
  // "Lo faccio ora" (re-enter that exercise) and "Posticipa" (just acknowledge).
  const events = state.inspector.events || [];
  const skipEvents = events.filter(e => e.kind === 'auto_skip' || e.kind === 'manual_skip');
  const skipsWrap = $('complete-skips');
  if (skipEvents.length === 0) {
    skipsWrap.innerHTML = '';
  } else {
    skipsWrap.innerHTML =
      '<div class="complete-skips-title">Saltati durante la sessione</div>'
      + skipEvents.map(ev => {
          const why = ev.kind === 'auto_skip'
            ? (ev.meta.trigger === 'neural_fatigue' ? 'fatica neurale' : 'fatica accumulata')
            : 'tua scelta';
          return '<div class="complete-skips-row">'
               + '<span class="ex-name">' + ev.exName + '<span style="display:block;font-weight:400;color:var(--riso-dim);font-size:10px;margin-top:2px;">' + why + '</span></span>'
               + '<button class="complete-skips-action" onclick="postSessionDoNow(\'' + ev.exId + '\')">Fallo ora</button>'
               + '</div>';
        }).join('');
  }

  inspectorLog('set', { tag: 'workout complete', msg: w.name + ' done', meta: 'rate=' + Math.round(rate*100) + '% sets=' + completed + '/' + prescribed });
  // Sprint 2.2 vero — chiudi la sessione in DB (status='completed', completed_at=now).
  // Fire-and-forget: se DB fallisce, l'UI mostra comunque la schermata complete.
  completeSession().catch(e => console.error('[db] completeSession error:', e));
  show('complete');
}

/* v6 §9 — Post-session override stub. Re-enters the exercise. */
function postSessionDoNow(exId) {
  const idx = state.workout.exercises.findIndex(e => e.id === exId);
  if (idx < 0) return;
  state.exIdx = idx; state.setIdx = 0; state.preloadedNullLoad = null;
  state.pendingAutoSkipFor = null; // disarm so we don't re-skip
  state.startedExerciseSequence.push(state.workout.exercises[idx].id);
  const ex = state.workout.exercises[idx];
  if (ex.sets.every(s => (s.kind === 'reps' || s.kind === 'open') && s.load === null) && ex.sets.length > 0) {
    return showPreload(ex);
  }
  showExecute();
}

/* STORY-030 helper — render a coach-report section */
function renderCoachSection(title, items, modifier) {
  const cls = modifier === 'degraded' ? ' degraded' : '';
  if (modifier === 'empty') {
    return '<div class="complete-coach-section">'
         + '<div class="complete-coach-section-h">' + title + '</div>'
         + '<div class="complete-coach-empty">' + items[0] + '</div></div>';
  }
  return '<div class="complete-coach-section">'
       + '<div class="complete-coach-section-h">' + title + '</div>'
       + items.map(t => '<div class="complete-coach-item' + cls + '">' + t + '</div>').join('')
       + '</div>';
}

function returnHome() {
  state.workout = null;
  state.selectedWorkoutId = null;
  show('home');
  renderHome();
}

/* -------- 12. INSPECTOR PANEL -------- */
let inspectorOpen = false;
let currentInspectorTab = 'state';
function toggleInspector() {
  inspectorOpen = !inspectorOpen;
  $('inspector').classList.toggle('open', inspectorOpen);
  $('inspector-fab').classList.toggle('active', inspectorOpen);
  if (inspectorOpen) renderInspector();
}
function switchInspectorTab(tab) {
  currentInspectorTab = tab;
  document.querySelectorAll('.inspector-tab').forEach(t => t.classList.toggle('active', t.dataset.tab === tab));
  $('inspector-pane-state').classList.toggle('hidden', tab !== 'state');
  $('inspector-pane-events').classList.toggle('hidden', tab !== 'events');
  $('inspector-pane-rules').classList.toggle('hidden', tab !== 'rules');
  renderInspector();
}
function flashBtn(btn, kind, label, holdMs) {
  if (!btn) return;
  const orig = btn.dataset.origLabel || btn.textContent;
  btn.dataset.origLabel = orig;
  btn.classList.add('flash-' + kind);
  btn.textContent = label;
  setTimeout(() => {
    btn.classList.remove('flash-' + kind);
    btn.textContent = orig;
  }, holdMs || 1200);
}
function clearInspector(btn) {
  state.inspector.log = [];
  state.inspector.rulesFired = {};
  renderInspector();
  flashBtn(btn, 'ok', 'Cleared ✓');
}
function copyInspectorLog(btn) {
  const json = JSON.stringify({ rulesFired: state.inspector.rulesFired, log: state.inspector.log }, null, 2);
  // Try modern clipboard API; on file:// it usually rejects so always have a fallback ready.
  let copied = false;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(json).then(
        () => flashBtn(btn, 'ok', 'Copied ✓'),
        () => fallbackCopy(json, btn)
      );
      return;
    }
  } catch (e) { /* fall through */ }
  fallbackCopy(json, btn);
}
function fallbackCopy(text, btn) {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.top = '0'; ta.style.left = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus(); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    if (ok) flashBtn(btn, 'ok', 'Copied ✓');
    else flashBtn(btn, 'err', 'Copy failed', 1600);
  } catch (e) {
    flashBtn(btn, 'err', 'Copy failed', 1600);
  }
}

function renderInspector() {
  // Sub
  if (state.workout) {
    const ex = currentEx();
    if (ex) $('inspector-sub').textContent = state.workout.name + ' · ' + ex.name + ' · set ' + ((currentSet()||{}).order || '—');
    else $('inspector-sub').textContent = state.workout.name;
  } else {
    $('inspector-sub').textContent = 'No active workout';
  }

  // STATE pane
  if (currentInspectorTab === 'state') {
    const ex = state.workout ? currentEx() : null;
    const goal = ex ? resolveGoal(ex, state.workout, PROGRAM) : (PROGRAM.goal || '—');
    const stackActive = neuralStackActive(state.startedExerciseSequence);
    const stackCount = (() => {
      let c = 0;
      for (let i = state.startedExerciseSequence.length-1; i>=0; i--) {
        const e = findExercise(state.startedExerciseSequence[i]);
        if (e && e.neuro_type === 'neural') c++; else break;
      }
      return c;
    })();
    const html =
      '<div class="inspector-section">'
      + '<div class="inspector-section-title">Resolved goal (DL-2)</div>'
      + '<div class="inspector-state">'
      +   '<b>program</b><span>' + (PROGRAM.goal || '—') + '</span>'
      +   '<b>workout</b><span>' + ((state.workout && state.workout.goal) || '— (inherits)') + '</span>'
      +   '<b>exercise</b><span>' + ((ex && ex.goal) || '— (inherits)') + '</span>'
      +   '<b>resolved</b><span><strong>' + goal + '</strong></span>'
      + '</div></div>'
      + '<div class="inspector-section">'
      + '<div class="inspector-section-title">Magnitudes (DL-3)</div>'
      + '<div class="inspector-state">'
      +   '<b>load_Δ</b><span>±' + (MAG[goal].load_pct*100) + '% rounded 0.5kg</span>'
      +   '<b>rep_Δ</b><span>±' + MAG[goal].rep + '</span>'
      +   '<b>rest_Δ</b><span>+' + MAG[goal].rest_s + 's</span>'
      +   '<b>severe</b><span>-' + (SEVERE_REDUCTION*100) + '% (DL-8, goal-indep)</span>'
      + '</div></div>'
      + '<div class="inspector-section">'
      + '<div class="inspector-section-title">Neural stack (§5 / DL-14)</div>'
      + '<div class="inspector-state">'
      +   '<b>chain</b><span>' + state.startedExerciseSequence.map(id=>{ const e = findExercise(id); return e?(e.name+(e.neuro_type==='neural'?'·N':'·–')):'?'; }).join(' → ') + '</span>'
      +   '<b>consecutive neural</b><span>' + stackCount + '</span>'
      +   '<b>active (≥3)</b><span>' + (stackActive ? '✓ YES — tolerance↓ + rest↑' : '— no') + '</span>'
      + '</div></div>'
      + '<div class="inspector-section">'
      + '<div class="inspector-section-title">Per-exercise flags</div>'
      + '<div class="inspector-state">'
      +   '<b>§2 fired</b><span>' + (Array.from(state.severeFiredFor).join(', ') || '—') + '</span>'
      +   '<b>§3 fired</b><span>' + (Array.from(state.cumulativeFiredFor).join(', ') || '—') + '</span>'
      +   '<b>§4 armed for</b><span>' + (state.pendingAutoSkipFor || '—') + '</span>'
      +   '<b>load overrides</b><span>' + (Object.keys(state.loadOverrides).length || '0') + '</span>'
      +   '<b>+rest queued</b><span>+' + state.pendingRestExtraSeconds + 's</span>'
      + '</div></div>'
      // ─── F0004 PS-1 — Volume aggregator (silent in v7.0) ───
      + (function(){
          const counters = state.weeklyVolume.thisSession;
          const rows = ['push','pull','squat','hinge','core','skill'].map(p => {
            const v = counters[p] || 0;
            const b = VOLUME_BANDS[p] || { mev: 0, mav: 0, mrv: 0 };
            const label = getVolumeBandLabel(p);
            const bandTxt = (b.mrv === 0)
              ? '— (TUT-driven, see PS-4b)'
              : 'MEV ' + b.mev + ' / MAV ' + b.mav + ' / MRV ' + b.mrv;
            const labelTxt = label === 'n/a' ? '—'
                           : label === 'below_mev' ? '↓ below MEV'
                           : label === 'mev_to_mav' ? '✓ MEV→MAV'
                           : label === 'mav_to_mrv' ? '⚠ MAV→MRV'
                           : '⚠⚠ above MRV';
            return '<b>' + p + '</b><span>' + v + ' hard sets · ' + labelTxt + ' &nbsp;<span style="color:var(--riso-dim);font-size:9px">' + bandTxt + '</span></span>';
          }).join('');
          return '<div class="inspector-section">'
               + '<div class="inspector-section-title">PS-1 weekly volume (this session)</div>'
               + '<div class="inspector-state">' + rows + '</div></div>';
        })()
      // ─── F0004 PS-4c — Joint readiness ───
      + (function(){
          const j = state.jointReadiness || {};
          const labels = { wrist:'polso', elbow:'gomito', shoulder:'spalla', lower_back:'lombare' };
          const rows = Object.keys(labels).map(k => {
            const s = j[k];
            const tag = s === 'dolore' ? '⚠⚠ dolore (skip)' : s === 'fastidio' ? '⚠ fastidio (-5%)' : '— ok';
            return '<b>' + labels[k] + '</b><span>' + tag + '</span>';
          }).join('');
          return '<div class="inspector-section">'
               + '<div class="inspector-section-title">PS-4c joint readiness</div>'
               + '<div class="inspector-state">' + rows + '</div></div>';
        })()
      // ─── F0004 PS-3 — Stimulus-typed recovery (last decision) ───
      + (function(){
          const d = state.lastInterExRestDecision;
          const cur = state.workout ? currentEx() : null;
          const curTxt = cur ? (cur.name + ' [' + cur.stimulus_type + ']') : '—';
          const dTxt = d
            ? (d.from + ' [' + d.fromStimulus + '] → ' + d.to + ' [' + d.toStimulus + ']'
               + ' · prog=' + d.programmed + 's → resolved=' + d.resolved + 's'
               + (d.programmed !== d.resolved ? ' (PS-3 floor)' : ' (coach honored)'))
            : '— (no inter-exercise rest decided yet)';
          return '<div class="inspector-section">'
               + '<div class="inspector-section-title">PS-3 stimulus + recovery</div>'
               + '<div class="inspector-state">'
               +   '<b>current ex</b><span>' + curTxt + '</span>'
               +   '<b>last inter-rest</b><span>' + dTxt + '</span>'
               +   '<b>floors</b><span>max_strength=' + STIMULUS_REST_FLOOR.max_strength
                   + '/180 · hyp=' + STIMULUS_REST_FLOOR.hypertrophy
                   + ' · skill=' + STIMULUS_REST_FLOOR.skill
                   + ' · cond=' + STIMULUS_REST_FLOOR.conditioning + 's</span>'
               + '</div></div>';
        })();
    $('inspector-pane-state').innerHTML = html;
  }

  // EVENTS pane
  if (currentInspectorTab === 'events') {
    if (state.inspector.log.length === 0) {
      $('inspector-pane-events').innerHTML = '<div class="inspector-empty">No events yet. Start a workout.</div>';
    } else {
      $('inspector-pane-events').innerHTML = state.inspector.log.map(e => {
        const cls = e.code && e.code.indexOf('§2') >= 0 ? 'severe'
                  : e.code && e.code.indexOf('§3') >= 0 ? 'cumul'
                  : e.code && e.code.indexOf('§5') >= 0 ? 'stack'
                  : e.code ? 'rule' : '';
        const time = new Date(e.t).toLocaleTimeString();
        return '<div class="inspector-event ' + cls + '">'
          + '<div class="inspector-event-head">'
          +   '<span class="inspector-event-tag">' + (e.tag || e.kind) + '</span>'
          +   '<span class="inspector-event-time">' + time + '</span>'
          + '</div>'
          + '<div class="inspector-event-msg">' + (e.msg || '') + '</div>'
          + (e.meta ? '<div class="inspector-event-meta">' + e.meta + '</div>' : '')
          + '</div>';
      }).join('');
    }
  }

  // RULES pane
  if (currentInspectorTab === 'rules') {
    const fired = Object.entries(state.inspector.rulesFired);
    if (fired.length === 0) {
      $('inspector-pane-rules').innerHTML = '<div class="inspector-empty">No rules have fired yet.</div>';
    } else {
      $('inspector-pane-rules').innerHTML =
        '<div class="inspector-section-title">Rules fired this session</div>'
        + '<div class="inspector-rule-list">'
        + fired.map(([k,v]) => '<div class="inspector-rule-row"><span class="inspector-rule-name">' + k + '</span><span class="inspector-rule-count">×' + v + '</span></div>').join('')
        + '</div>';
    }
  }
}

/* -------- 12.5 AUTH (Sprint 1.4 — magic link via Supabase + Resend) -------- *
 * Strategia invite-only (DL-37): solo email gia' presenti in auth.users (provisioning
 * via Sprint 2.1 seed-early-adopters script o manual Auth dashboard invite) possono
 * autenticarsi. signInWithOtp per un'email non-esistente ritorna errore
 * "Signups not allowed for otp" -> messaggio user-friendly "Contatta angelo per invite".
 * Bootstrap flow:
 *   splash 1.6s -> dismissSplash() -> getSession()
 *     se session -> HOME
 *     se no     -> LOGIN
 * Magic link callback flow:
 *   utente clicca link nella mail -> redirect /?access_token=...
 *   client init parsa URL (detectSessionInUrl=true) -> setSession -> emette SIGNED_IN
 *   listener onAuthStateChange -> transition login->home (se mai era visibile)
 */

const SPLASH_FADE_MS = 350;

function getScreen(id) {
  return document.getElementById(id);
}

function activateScreen(id) {
  const el = getScreen(id);
  if (!el) return;
  el.style.display = '';
  el.classList.remove('fade-out');
  el.classList.add('active');
}

function deactivateScreen(id) {
  const el = getScreen(id);
  if (!el) return;
  el.classList.add('fade-out');
  setTimeout(() => {
    el.classList.remove('active');
    el.style.display = 'none';
  }, SPLASH_FADE_MS);
}

function showLogin() {
  deactivateScreen('screen-home');
  activateScreen('screen-login');
}

function showHome() {
  deactivateScreen('screen-login');
  activateScreen('screen-home');
}

async function submitLogin() {
  const emailInput = document.getElementById('login-email');
  const submitBtn = document.getElementById('login-submit');
  const feedback = document.getElementById('login-feedback');
  if (!emailInput || !submitBtn || !feedback) return;

  const email = emailInput.value.trim().toLowerCase();
  if (!email) return;

  // UI loading state
  submitBtn.disabled = true;
  const originalLabel = submitBtn.textContent;
  submitBtn.textContent = 'Invio in corso...';
  feedback.className = 'login-feedback';
  feedback.textContent = '';

  try {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        // Redirect dopo click sul magic link nella mail.
        // Su prod = https://getlynar.app/, su dev = http://localhost:5173/.
        // Entrambi sono gia' in Supabase Auth Redirect URLs allowlist (config 2026-05-13).
        emailRedirectTo: window.location.origin + '/'
      }
    });

    if (error) {
      // Messaggi user-friendly per casi noti.
      const msg = (error.message || '').toLowerCase();
      if (msg.includes('signups not allowed') || msg.includes('signup is disabled') || msg.includes('signups disabled')) {
        feedback.className = 'login-feedback error';
        feedback.innerHTML = 'Email non riconosciuta.<br/>Contatta angelo.sala@lynar.it per essere invitato.';
      } else if (msg.includes('rate limit') || msg.includes('too many')) {
        feedback.className = 'login-feedback error';
        feedback.textContent = 'Troppi tentativi. Riprova tra qualche minuto.';
      } else {
        feedback.className = 'login-feedback error';
        feedback.textContent = 'Errore: ' + error.message;
      }
      console.error('[auth] signInWithOtp error:', error);
    } else {
      feedback.className = 'login-feedback success';
      feedback.innerHTML = 'Magic link inviato.<br/>Controlla la tua email e clicca il link per accedere.';
      emailInput.value = '';
    }
  } catch (e) {
    feedback.className = 'login-feedback error';
    feedback.textContent = 'Errore di rete. Riprova.';
    console.error('[auth] signInWithOtp exception:', e);
  } finally {
    submitBtn.disabled = false;
    submitBtn.textContent = originalLabel;
  }
}

async function logoutLynar() {
  // Sprint 1.4 minimo: callable da console (window.logoutLynar()).
  // Sprint futuro: bottone visibile nella HOME (es. tap su home-athlete).
  const { error } = await supabase.auth.signOut();
  if (error) {
    console.error('[auth] signOut error:', error);
  } else {
    console.log('[auth] signed out.');
  }
  // Il listener onAuthStateChange catchera' SIGNED_OUT e mostrera' LOGIN.
}


/* ─────────────────────── SPRINT 2.2 — LOAD PROGRAM FROM DB ─────────────────────── *
 * Quando l'atleta logga, sostituisce il fallback `PROGRAM` (sample.json embed)
 * col programma reale letto da Supabase.
 *
 * Flow:
 *   1. select profile.display_name per athlete name nell'UI
 *   2. select programs WHERE athlete_id = auth.uid() AND active = true
 *   3. costruisce raw shape compatibile con normalizeSample()
 *   4. normalizza, riassegna PROGRAM, re-render HOME
 *
 * Failure modes (tutti silenziosi, fallback al sample):
 *   - No session                     → return early
 *   - Network/RLS error              → console.error + fallback
 *   - Profile o programs row missing → console.warn + fallback
 *   - data shape non normalizabile   → caught, log + fallback
 *
 * I dati in `program.data` sono attesi nella shape legacy v7.x (workouts[] con
 * exercise_prescriptions[] + set_blocks[]). Per la migration del payload vedi
 * `inputs/seed-programs/angelo.json` (Sprint 2.2 step 1).
 */
/* ─────────────────────── SPRINT 2.2 vero + 2.3 — DB WRITES ─────────────────────── *
 * Persistence layer: sessions + set_logs.
 *
 * Design:
 *   - Fire-and-forget writes: la UI non aspetta il DB, evita hang/latency
 *   - Client-generated UUIDs (crypto.randomUUID) come idempotency key: se un
 *     retry loggasse due volte lo stesso set con lo stesso client_set_id, la
 *     seconda scrittura fallisce per unique constraint (o comunque non
 *     duplica il record concettuale). Oggi non ancora enforced a livello di
 *     DB, ma il campo e' li per l'aggancio a Sprint 2.6 offline sync.
 *   - Raw fetch (stessa scelta di loadProgramFromDB per il bug supabase-js).
 *   - Module-level `_dbSession` / `_currentProgramId` / `_currentSessionId`:
 *     stato del ciclo di scrittura corrente, aggiornato dai vari entry point
 *     (auth listener, loadProgramFromDB, startWorkout, finishWorkout).
 *   - Nessun blocco su fallimento: se il write fallisce, l'UI continua a
 *     funzionare (in-memory come v7.x), errore in console per debug.
 */
let _dbSession = null;
let _currentProgramId = null;
let _currentSessionId = null;
let _clientSessionId = null;
// Sprint 2.5 — suggested workout basato sull'ultimo completamento in DB.
// Ha priorita' su pickDefaultWorkout calendar-based (che rimane come fallback).
let _dbSuggestedWorkoutId = null;

async function dbInsert(table, row) {
  if (!_dbSession) { console.warn('[db] insert', table, 'no session'); return null; }
  const baseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  try {
    const res = await fetch(baseUrl + '/rest/v1/' + table, {
      method: 'POST',
      headers: {
        apikey: anonKey,
        Authorization: 'Bearer ' + _dbSession.access_token,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(row)
    });
    if (!res.ok) {
      const body = await res.text();
      console.error('[db] insert ' + table + ' HTTP ' + res.status, body);
      return null;
    }
    const rows = await res.json();
    return rows[0] || null;
  } catch (e) {
    console.error('[db] insert ' + table + ' network error:', e);
    return null;
  }
}

async function dbUpdate(table, id, patch) {
  if (!_dbSession) { console.warn('[db] update', table, 'no session'); return null; }
  const baseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  try {
    const res = await fetch(baseUrl + '/rest/v1/' + table + '?id=eq.' + id, {
      method: 'PATCH',
      headers: {
        apikey: anonKey,
        Authorization: 'Bearer ' + _dbSession.access_token,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(patch)
    });
    if (!res.ok) {
      const body = await res.text();
      console.error('[db] update ' + table + ' HTTP ' + res.status, body);
      return null;
    }
    const rows = await res.json();
    return rows[0] || null;
  } catch (e) {
    console.error('[db] update ' + table + ' network error:', e);
    return null;
  }
}

/* Insert una riga sessions e memorizza id per set_logs downstream. */
async function insertSession() {
  if (!_dbSession || !_currentProgramId || !state.workout) {
    console.warn('[db] insertSession: missing prereqs',
      { hasSession: !!_dbSession, programId: _currentProgramId, hasWorkout: !!state.workout });
    return null;
  }
  _clientSessionId = crypto.randomUUID();
  const row = {
    athlete_id: _dbSession.user.id,
    program_id: _currentProgramId,
    week_index: state.workout.week || 1,
    workout_day_name: state.workout.name || state.workout.day || 'Session',
    status: 'in_progress',
    started_at: new Date().toISOString(),
    client_session_id: _clientSessionId
  };
  const inserted = await dbInsert('sessions', row);
  if (inserted && inserted.id) {
    _currentSessionId = inserted.id;
    console.log('[db] session created:', _currentSessionId, '·', row.workout_day_name);
  }
  return inserted;
}

/* Insert una riga set_logs. Chiamata da logSetAsPrescribed + confirmFatigue. */
async function insertSetLog(entry, ex, s) {
  if (!_dbSession) { console.warn('[db] insertSetLog no session — skip'); return null; }
  if (!_currentSessionId) { console.warn('[db] insertSetLog no active session — skip'); return null; }
  const kindMap = { reps: 'rep_based', duration: 'duration', open: 'open_ended' };
  const dbKind = kindMap[entry.kind] || null;
  const row = {
    session_id: _currentSessionId,
    athlete_id: _dbSession.user.id,
    exercise_name: ex.name,
    exercise_category: ex.category || null,
    movement_pattern: ex.movement_pattern || null,
    stimulus_type: ex.stimulus_type || null,
    set_index: entry.order,
    set_kind: dbKind,
    prescribed_reps: entry.kind === 'reps' ? entry.target : null,
    actual_reps: (entry.kind === 'reps' || entry.kind === 'open') ? entry.actual : null,
    prescribed_load_kg: (s && s.load !== undefined && s.load !== null) ? s.load : null,
    actual_load_kg: (entry.load !== undefined && entry.load !== null) ? entry.load : null,
    prescribed_duration_seconds: entry.kind === 'duration' ? entry.target : null,
    actual_duration_seconds: entry.kind === 'duration' ? entry.actual : null,
    effort: entry.effort || null,
    technique: entry.tech || null,
    cedimento: !!entry.fatigue_path,
    client_set_id: crypto.randomUUID()
  };
  const inserted = await dbInsert('set_logs', row);
  if (inserted) {
    console.log('[db] set_log:', ex.name, 'set', entry.order, entry.kind,
      'actual=', entry.actual, 'load=', entry.load);
  }
  return inserted;
}

/* Sprint 2.5 — Interroga le sessions completate per determinare il prossimo
 * workout suggerito in base al cycle-based model (DL-33).
 *
 * Algoritmo: per ogni workout in PROGRAM.workouts, trova last completed_at
 * dalle sessions di questo athlete/program. Il workout con il tempo di
 * completamento piu' vecchio (o mai completato) e' il prossimo.
 *
 * Tie-break: se piu' workout hanno lo stesso stato (es. nessuno mai completato),
 * preferisce quello con day che matcha oggi. Fallback: primo in ordine.
 *
 * Ritorna workout.id o null. Nessuna eccezione: fail-silent con log.
 */
async function computeNextWorkoutIdFromDB() {
  if (!_dbSession || !_currentProgramId) {
    console.log('[program] computeNext: no session or program — skip');
    return null;
  }
  if (!PROGRAM || !PROGRAM.workouts || PROGRAM.workouts.length === 0) {
    return null;
  }
  const baseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  try {
    const res = await fetch(
      baseUrl + '/rest/v1/sessions?program_id=eq.' + _currentProgramId
        + '&status=eq.completed&select=workout_day_name,completed_at'
        + '&order=completed_at.desc',
      {
        headers: {
          apikey: anonKey,
          Authorization: 'Bearer ' + _dbSession.access_token
        }
      }
    );
    if (!res.ok) {
      const body = await res.text();
      console.error('[program] computeNext HTTP', res.status, body);
      return null;
    }
    const rows = await res.json();
    console.log('[program] computeNext: found', rows.length, 'completed sessions');

    // Ultimo completed_at per workout_day_name (usa PROGRAM.workouts.name come chiave)
    const lastCompletedAt = {};
    for (const w of PROGRAM.workouts) {
      lastCompletedAt[w.name] = null;
    }
    for (const s of rows) {
      const key = s.workout_day_name;
      if (lastCompletedAt[key] === undefined) continue; // workout non piu' nel program (edge case)
      const t = new Date(s.completed_at).getTime();
      const prev = lastCompletedAt[key] ? new Date(lastCompletedAt[key]).getTime() : -Infinity;
      if (t > prev) lastCompletedAt[key] = s.completed_at;
    }
    console.log('[program] computeNext lastCompletedAt:', lastCompletedAt);

    // Pick con MIN(last completed_at). null (mai fatto) vince su qualsiasi timestamp.
    // Tie-break: today calendar match; poi first-in-order.
    const today = DAY_IT[
      ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][new Date().getDay()]
    ];
    let candidates = PROGRAM.workouts.map(w => ({
      w,
      t: lastCompletedAt[w.name] === null ? -Infinity : new Date(lastCompletedAt[w.name]).getTime()
    }));
    const minT = Math.min(...candidates.map(c => c.t));
    candidates = candidates.filter(c => c.t === minT);
    // Tie-break: today's day wins
    const todayMatch = candidates.find(c => c.w.day === today);
    const winner = todayMatch ? todayMatch.w : candidates[0].w;
    console.log('[program] computeNext:', winner.name, '(day=' + winner.day + ')');
    return winner.id;
  } catch (e) {
    console.error('[program] computeNext error:', e);
    return null;
  }
}

/* Update sessions row a status=completed. Chiamata da finishWorkout. */
async function completeSession() {
  if (!_dbSession || !_currentSessionId) return null;
  const patch = {
    status: 'completed',
    completed_at: new Date().toISOString()
  };
  const updated = await dbUpdate('sessions', _currentSessionId, patch);
  if (updated) console.log('[db] session completed:', _currentSessionId);
  const prev = _currentSessionId;
  _currentSessionId = null;
  _clientSessionId = null;
  // Sprint 2.5 — ricomputa il prossimo workout suggerito dopo il completamento.
  // La HOME successiva mostrera' l'altro workout (o comunque quello meno recente).
  computeNextWorkoutIdFromDB().then(nextId => {
    if (nextId) {
      _dbSuggestedWorkoutId = nextId;
      console.log('[program] next suggestion updated post-completion');
    }
  }).catch(e => console.error('[program] computeNext post-complete error:', e));
  return updated;
}


// Idempotency state per Sprint 2.2:
//   - _inFlight: blocca re-entrancy se il fetch e' ancora in volo
//   - _loadedForUserId: skip se gia' caricato per questo utente
// HMR + onAuthStateChange duplicato (SIGNED_IN multipli + INITIAL_SESSION) facevano
// 4+ fetch per page load e poi visual glitch (renderHome multiplo concurrent con
// screen overlays attivi → DOM corrotto). Vedi friction G2.
let _loadProgramInFlight = false;
let _loadedProgramForUserId = null;

async function loadProgramFromDB(session) {
  if (!session) {
    console.log('[program] no session — keeping sample fallback');
    return false;
  }
  // Re-entrancy guard
  if (_loadProgramInFlight) {
    console.log('[program] load already in-flight — skip duplicate');
    return false;
  }
  // Already-loaded guard (same user, program present)
  if (_loadedProgramForUserId === session.user.id
      && PROGRAM && PROGRAM.workouts && PROGRAM.workouts.length > 0
      && PROGRAM.name !== 'Winter Trainer Demo Program') {
    console.log('[program] already loaded for', session.user.email, '— skip');
    return false;
  }
  _loadProgramInFlight = true;
  console.log('[program] loading from DB for', session.user.email);

  // NB Sprint 2.2 (2026-05-29): usiamo raw fetch invece di supabase.from().select()
  // perche' supabase-js v2.105.4 ha un bug per cui auth.getSession() e .from()
  // queries restano in Promise pending forever dopo SIGNED_IN del magic link
  // callback (loop interno di auth refresh). La raw fetch col Bearer token
  // estratto dalla session evita completamente il blocco. Auth login/logout/
  // refresh resta su supabase-js. Da rivedere se future versioni di supabase-js
  // risolvono il problema.
  const baseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const accessToken = session.access_token;
  const headers = {
    apikey: anonKey,
    Authorization: 'Bearer ' + accessToken,
    Accept: 'application/json'
  };

  try {
    // Profile (display_name)
    const profileRes = await fetch(
      baseUrl + '/rest/v1/profiles?id=eq.' + session.user.id + '&select=id,display_name,persona,locale',
      { headers }
    );
    if (!profileRes.ok) {
      const body = await profileRes.text();
      console.error('[program] profile fetch HTTP', profileRes.status, body);
      return false;
    }
    const profiles = await profileRes.json();
    const profile = profiles[0] || null;

    // Active program
    const progRes = await fetch(
      baseUrl + '/rest/v1/programs?athlete_id=eq.' + session.user.id
        + '&active=eq.true&select=id,name,goal,data,source',
      { headers }
    );
    if (!progRes.ok) {
      const body = await progRes.text();
      console.error('[program] programs fetch HTTP', progRes.status, body);
      return false;
    }
    const progs = await progRes.json();
    const prog = progs[0] || null;

    if (!prog) {
      console.warn('[program] no active program for athlete — keeping sample fallback');
      return false;
    }

    // Costruisci raw shape compatibile con normalizeSample().
    // program.data ha gia' duration_weeks, current_week, workouts[] in shape legacy.
    // Le column-level fields (name, goal) overridano eventuali duplicati in data.
    const raw = {
      athlete: { name: (profile && profile.display_name) || session.user.email || 'Atleta' },
      program: {
        ...prog.data,
        name: prog.name,
        goal: prog.goal || 'mixed'
      }
    };

    const newProgram = normalizeSample(raw);
    PROGRAM = newProgram;
    _loadedProgramForUserId = session.user.id;
    _currentProgramId = prog.id;         // Sprint 2.2 vero — memorizzo id per sessions.program_id FK
    _dbSession = session;                // Sprint 2.2 vero — session token corrente per write path

    // Sprint 2.5 — dopo aver caricato PROGRAM, calcola il prossimo workout dai
    // completamenti storici. Non-blocking rispetto al render: il primo renderHome
    // sotto usera' il fallback calendar-based, poi (fra ~200ms) il DB suggeriment
    // arriva e ri-renderizziamo con la scelta corretta.
    computeNextWorkoutIdFromDB().then(nextId => {
      if (nextId && nextId !== _dbSuggestedWorkoutId) {
        _dbSuggestedWorkoutId = nextId;
        const homeScreen = document.getElementById('screen-home');
        if (homeScreen && homeScreen.classList.contains('active')) {
          renderHome();
        }
      }
    }).catch(e => console.error('[program] computeNext initial error:', e));
    console.log('[program] loaded:', PROGRAM.name, '·', PROGRAM.workouts.length, 'workouts');

    // Reset selection + re-render HOME col programma vero — SOLO se siamo
    // attualmente su HOME. Se l'atleta e' gia' migrato verso EXEC/REST/etc.,
    // un renderHome forzato corromperebbe lo screen state (vedi friction G2:
    // preload kg overlay sopra HOME card). Sprint 2.2 fix idempotency 2026-05-29.
    state.selectedWeek = PROGRAM.current_week || 1;
    state.selectedWorkoutId = null;
    const homeScreen = document.getElementById('screen-home');
    if (homeScreen && homeScreen.classList.contains('active')) {
      renderHome();
    }
    return true;
  } catch (e) {
    console.error('[program] loadProgramFromDB exception:', e);
    return false;
  } finally {
    _loadProgramInFlight = false;
  }
}


// Listener auth state. Subscribed al module load — riceve INITIAL_SESSION subito.
// I diversi event types:
//   INITIAL_SESSION  - emesso una volta al subscribe, session puo' essere null o presente.
//                      Importante: per magic link di tipo INVITE (admin "Send invitation"
//                      dashboard), supabase-js puo' emettere INITIAL_SESSION (non SIGNED_IN)
//                      quando parsa il fragment URL. Se non gestito qui, l'utente atterra
//                      su getlynar.app autenticato ma vede LOGIN.
//   SIGNED_IN        - login completato (signInWithOtp callback per type=magiclink, etc.)
//   SIGNED_OUT       - logout esplicito o token revocato
//   TOKEN_REFRESHED  - refresh automatico ogni ~1h, no UI change
//   USER_UPDATED     - cambio password/email, no UI change
//   PASSWORD_RECOVERY - flow di recovery password, non usato qui (magic-link-only)
// Fix 2026-05-18 (incident Giulia magic link invite): aggiunto handling
// INITIAL_SESSION-with-session in addition a SIGNED_IN.
supabase.auth.onAuthStateChange(async (event, session) => {
  console.log('[auth] event:', event, 'session:', session ? 'present' : 'null');
  // Caso login: sia magic link signin (SIGNED_IN) sia magic link invite via admin
  // dashboard (INITIAL_SESSION con session presente) triggerano transizione a HOME.
  if (session && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) {
    showHome();
    // Sprint 2.2: rimpiazza il PROGRAM sample col programma reale dell'atleta.
    // Async, non-blocking — l'utente vede la HOME col sample (gia' pre-renderizzata
    // su DOMContentLoaded) e poi si aggiorna quando il fetch completa (~200-500ms).
    await loadProgramFromDB(session);
  } else if (event === 'SIGNED_OUT') {
    showLogin();
    // Ripristina sample fallback in modo che logout + nuovo login non conservi
    // il programma del precedente utente (edge case multi-account su stesso device).
    // Reset anche del guard idempotency per permettere reload alla prossima auth.
    PROGRAM = normalizeSample(RAW_SAMPLE);
    _loadedProgramForUserId = null;
    _dbSession = null;
    _currentProgramId = null;
    _currentSessionId = null;
    _clientSessionId = null;
    _dbSuggestedWorkoutId = null;
    renderHome();
  } else if (event === 'TOKEN_REFRESHED' && session) {
    // Sprint 2.2 vero — mantengo il token corrente allineato senza re-fetching programma
    _dbSession = session;
  }
});


/* -------- 13. INIT -------- */

// Durata splash screen prima della transizione a HOME o LOGIN.
// Spec: product/design/riso/lynar-y-system.html §D2.
// Bypass: ?nosplash=1 nell'URL (utile durante dev / e2e test).
const SPLASH_DURATION_MS = 1600;

async function dismissSplash() {
  const splash = document.getElementById('screen-splash');
  if (!splash) return;

  // Determina destinazione post-splash: HOME se autenticato, LOGIN altrimenti.
  // getSession() ritorna la session corrente (da localStorage o appena settata
  // da magic link callback se l'URL conteneva il token).
  let session = null;
  try {
    const result = await supabase.auth.getSession();
    session = result.data?.session || null;
  } catch (e) {
    console.error('[auth] getSession error:', e);
  }

  // Fade-out splash + activate destination
  splash.classList.add('fade-out');
  if (session) {
    activateScreen('screen-home');
  } else {
    activateScreen('screen-login');
  }

  // Cleanup dopo transizione: rimuovi splash dal flow
  setTimeout(() => {
    splash.classList.remove('active');
    splash.style.display = 'none';
  }, SPLASH_FADE_MS);
}

window.addEventListener('DOMContentLoaded', () => {
  state.selectedWeek = PROGRAM.current_week || 1;
  // v6 §3 — Inspector and debug FAB are hidden unless explicitly opted into via ?debug=1
  let debugMode = false;
  let skipSplash = false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('debug') === '1') {
      document.body.classList.add('debug');
      debugMode = true;
    }
    // Dev/test bypass: ?nosplash=1 salta direttamente a HOME (o LOGIN) senza brand moment
    if (params.get('nosplash') === '1') skipSplash = true;
  } catch (_) { /* file:// or older runtime — leave default (hidden) */ }

  // Pre-render HOME in background mentre lo splash e' visibile. Il pre-render
  // non e' problematico perche' HOME ha .active = false; sara' la dismissSplash()
  // a flippare la visibilita' con una transizione istantanea.
  // Nota: renderHome puo' essere chiamato anche senza session (mostra il sample
  // program di v7.3). In Sprint 2.x sara' rimpiazzato da load del programma
  // reale dell'atleta dalla tabella `programs`.
  renderHome();
  if (debugMode) renderInspector();

  if (skipSplash) {
    dismissSplash();
  } else {
    setTimeout(dismissSplash, SPLASH_DURATION_MS);
  }
});


/* ─────────────────────── 14. WINDOW EXPOSURE FOR INLINE HANDLERS ─────────────────────── *
 * Fix port-to-ES-module (Sprint 0.1): v7.3 originale era un singolo HTML con inline
 * <script> (global scope), quindi inline onclick="funcName()" trovava le funzioni su window.
 * Adesso main.js e' un ES module (type="module") -> le funzioni top-level sono module-scoped,
 * non globali. Senza questo blocco, ogni bottone con onclick="..." nell'HTML fallisce silently
 * con "funcName is not defined".
 *
 * Lista ricavata da `grep onclick= index.html src/main.js`. Se aggiungi nuovi inline handler
 * nell'HTML, ricordati di esporre anche la nuova funzione qui sotto.
 *
 * Long-term (Sprint 3.3 refactoring): rimpiazzeremo onclick inline con addEventListener
 * in moduli separati, e questo blocco diventera' inutile. Per ora e' la rete di salvataggio.
 */
Object.assign(window, {
  // Auth (Sprint 1.4)
  submitLogin, logoutLynar,
  // Workout start / navigation
  startWorkout, openOtherDays, closeOtherDays, returnHome,
  // Check-in (joint readiness)
  confirmCheckinOk, tapSomethingHurts, confirmCheckinDetailed, resetCheckin, pickJointStatus,
  // Set execution (rep-based)
  tapDone, editLoadInline, saveLoadEdit, dismissLoadEdit,
  // Set execution (fatigue / failure)
  confirmFatigue, cancelFatigue,
  // Set execution (null-load + open-ended overlays)
  confirmNullLoad, dismissNullOverlay, confirmOpenReps, dismissOpenOverlay,
  // Rest screen
  promptSkipRest, skipRest, cancelSkipRest,
  // Preload (pre-exercise load entry)
  confirmPreload,
  // Set execution (hold-to-fail mouse/touch handlers)
  failHoldStart, failHoldCancel,
  // Workout complete (post-session)
  postSessionDoNow,
  // Inspector debug (gated ?debug=1)
  toggleInspector, switchInspectorTab, clearInspector, copyInspectorLog,
});

